<template>
  <section :data-view-ready="!loading">
    <div
      v-if="loading"
      class="row">
      <div class="col text-center">
        <coozzy-spinner class="m-3" />
      </div>
    </div>
    <div
      v-else>
      <!-- Heading -->
      <div class="row justify-content-between align-items-center">
        <div class="col-auto">
          <h1 class="wrap">
            {{ pageTitle }}
          </h1>
        </div>
      </div>
      <div
        v-if="showTicketPreview">
        <ticket-details-view
          :order-ticket="ticketPreview"
          @checklist-note-changed="checklistNoteChanged"
          @checklist-assignee-changed="checklistAssigneeChanged"
          @checklist-status-changed="checklistStatusChanged"
          @watchers-changed="watchersChanged" />
      </div>
      <coozzy-card
        v-if="!showTicketPreview">
        <div class="row">
          <div class="col-12 col-md-8 mb-3">
            <div class="row mb-2">
              <div class="col-12 col-md-3">
                <label>{{ $t('message.ticketOverview.ticket.type.title') }}*</label>
                <coozzy-form-select
                  v-model="ticket.type"
                  data-cy="ticket-select-type"
                  :disabled="ticket.id?.startsWith('ticket_')"
                  :state="$v.ticket.type.$dirty && $v.ticket.type.$error ? false : null"
                  class="bg-init">
                  <option
                    v-for="item in availableTypes"
                    :key="item"
                    :value="item">
                    {{ $t('message.ticketOverview.ticket.type.' + item) }}
                  </option>
                </coozzy-form-select>
              </div>
            </div>
            <div class="row mb-2">
              <div class="col-12">
                <coozzy-form-input
                  id="title"
                  v-model="ticket.title"
                  data-cy="ticket-input-title"
                  :name="$t('message.ticketOverview.ticket.title') + '*'"
                  :state="$v.ticket.title.$dirty && $v.ticket.title.$error ? false : null"
                  type="text" />
              </div>
            </div>
            <div class="row">
              <div class="col-12 mb-2">
                <template v-if="ticket.type === 'TICKET_TYPE_CHECKLIST'">
                  <label>{{ $t('message.settings.template.checklist.title') }}</label>
                </template>
                <template v-else>
                  <label>{{ $t('message.settings.template.titleTicket') }}
                    <span class="ml-1">
                      <coozzy-info-circle-icon
                        v-b-tooltip.hover.html="$t('message.settings.template.selectConnectionsFirst')" />
                    </span>
                  </label>
                </template>
                <coozzy-form-select
                  v-model="selectedTemplate"
                  data-cy="ticket-select-template"
                  name="numberEntries">
                  <option
                    value="">
                    -
                  </option>
                  <option
                    v-for="template in getSortedTemplateForView()"
                    :key="template.id"
                    :value="template.id">
                    {{ template.name }}
                  </option>
                </coozzy-form-select>
              </div>
              <div
                v-if="ticket.type !== 'TICKET_TYPE_CHECKLIST'"
                class="col-12 mb-2">
                <label for="title">{{ $t('message.ticketOverview.ticket.description') }}</label>
                <coozzy-form-textarea
                  id="description"
                  ref="newTicketTextArea"
                  v-model="ticket.description"
                  data-cy="ticket-textarea-description"
                  :initial="ticket.description"
                  :placeholder="$t('message.ticketOverview.ticket.description')"
                  :rows="13"
                  :show-label="false"
                  :text="ticket.description"
                  :value-text-area="ticket.description"
                  name="create-ticket-description" />
              </div>
            </div>
            <div class="row mb-2">
              <div
                v-if="ticket.type === 'TICKET_TYPE_CHECKLIST'"
                class="col-12">
                <div class="row">
                  <div class="col-12">
                    <label>{{ $t('message.ticketOverview.ticket.todo') }}</label>
                    <coozzy-form-tag
                      v-model="initialTasks"
                      data-cy="ticket-input-checklist"
                      :initial="initialTasks"
                      :from-checklist="true"
                      @input="checklistAdded"
                      @suggestion-removed="suggestionRemoved" />
                  </div>
                  <div
                    v-if="listTasks.length > 0"
                    class="col-12 mt-2">
                    <draggable
                      v-model="listTasks"
                      @end="updateOrder">
                      <template
                        v-for="task in listTasks">
                        <div
                          :key="`task-${task.id}`"
                          class="row item-task ml-0 mr-0">
                          <div class="col-3">
                            <coozzy-form-input
                              v-if="editingTask && editingTask.id === task.id"
                              v-model="task.title"
                              data-cy="ticket-checklist-input-title"
                              class="input-task"
                              @blur="finishEdit(task)"
                              @keyup.enter="finishEdit(task)" />
                            <p v-else>
                              {{ task.title }}
                            </p>
                          </div>
                          <div class="col-3">
                            <template v-if="!editingTaskNote || (editingTaskNote && editingTaskNote.id !== task.id)">
                              <coozzy-button
                                v-if="task.note !== ''"
                                v-b-tooltip.hover.html="task.note"
                                design="transparent"
                                class="p-0 mb-1 ml-2 float-right"
                                data-cy="ticket-checklist-add-note-btn"
                                @click="editTaskNote(task)">
                                <coozzy-comment-icon-img />
                              </coozzy-button>
                              <coozzy-button
                                v-else
                                v-b-tooltip.hover.html="task.note"
                                design="transparent"
                                class="border float-right"
                                data-cy="ticket-checklist-add-note-btn"
                                @click="editTaskNote(task)">
                                {{ $t('message.ticketOverview.ticket.addNote') }}
                              </coozzy-button>
                            </template>
                            <div
                              v-if="editingTaskNote && editingTaskNote.id === task.id"
                              class="container_textArea">
                              <coozzy-form-textarea
                                v-model="task.note"
                                data-cy="ticket-checklist-textarea-note"
                                :initial="task.note"
                                class="w-100"
                                autofocus
                                :placeholder="$t('message.ticketOverview.ticket.writeNote')"
                                :rows="3"
                                :show-label="false"
                                :text="task.note"
                                :value-text-area="task.note"
                                @keydown.enter.prevent="saveTaskNote" />
                              <coozzy-button
                                class="icon"
                                design="primary"
                                data-cy="ticket-checklist-save-note-btn"
                                @click="saveTaskNote(null)">
                                <coozzy-enter-icon
                                  name="enterIcon"
                                  :active="true" />
                              </coozzy-button>
                            </div>
                          </div>
                          <div class="col-3">
                            <v-select
                              :key="task.id"
                              v-model="task.assignee"
                              data-cy="ticket-checklist-select-assignee"
                              :filter="searchAssignee"
                              :options="listAllEmployees"
                              class="assignee"
                              :components="{ Deselect: CustomDeselect }">
                              <template #option="option">
                                <template v-if="option.value">
                                  <img
                                    :alt="option.label"
                                    :src="option.value.profile.pictureUrl !== '' ? option.value.profile.pictureUrl : '/avatar.png'"
                                    class="align-self-center avatar"
                                    height="15px">
                                  {{ option.label }} <b>{{ option.company ? '-' + option.company : '' }}</b>
                                </template>
                              </template>
                              <template #selected-option="option">
                                <template v-if="option.value">
                                  <div>
                                    <img
                                      :alt="option.label"
                                      :src="option.value.profile.pictureUrl !== '' ? option.value.profile.pictureUrl : '/avatar.png'"
                                      class="align-self-center avatar"
                                      height="15">
                                    {{ option.label }} <b>{{ option.company ? '-' + option.company : '' }}</b>
                                  </div>
                                </template>
                              </template>
                            </v-select>
                          </div>
                          <div class="col-2">
                            <coozzy-form-select
                              v-model="task.status"
                              data-cy="ticket-checklist-select-status"
                              :class="`status-design ${getChecklistStatusClass(task.status)}`">
                              <option
                                v-for="status in availableStatusChecklist"
                                :key="status"
                                :value="status">
                                {{ $t('message.ticketOverview.checklistStatus.' + status) }}
                              </option>
                            </coozzy-form-select>
                          </div>
                          <div class="col-1">
                            <coozzy-button
                              v-if="!(editingTask && editingTask.id === task.id)"
                              design="transparent"
                              class="p-0 ml-2 float-right"
                              data-cy="ticket-checklist-edit-btn"
                              @click="editTask(task)">
                              <coozzy-edit-icon />
                            </coozzy-button>
                            <coozzy-button
                              v-if="editingTask && editingTask.id === task.id"
                              design="transparent"
                              class="p-0 ml-2 float-right"
                              data-cy="ticket-checklist-save-btn"
                              @click="finishEdit(task)">
                              <coozzy-save-icon />
                            </coozzy-button>
                            <coozzy-button
                              v-else
                              design="transparent"
                              class="p-0 ml-1  float-right"
                              data-cy="ticket-checklist-delete-btn"
                              @click="deleteTask(task)">
                              <coozzy-delete-icon />
                            </coozzy-button>
                          </div>
                        </div>
                      </template>
                    </draggable>
                  </div>
                </div>
              </div>
            </div>
            <div class="row mb-2">
              <div class="col-12">
                <label for="attachment"> {{ $t('message.ticketOverview.ticket.attachment') }}
                  ({{ getFilteredMediaListForView().length }})</label>
                <coozzy-form-file-input
                  id="file"
                  ref="coozzy-file-input"
                  data-cy="ticket-input-files"
                  :disabled="uploading"
                  accept="text/plain, application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.ms-excel ,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet
      ,application/vnd.ms-powerpoint,  application/vnd.openxmlformats-officedocument.presentationml.presentation, application/pdf,   image/*, video/*, .doc, .docx, .xls, .xlsx, .ppt, .pptx .pdf"
                  multiple
                  @change="addFile" />
                <div class="row mt-2 m-0">
                  <div class="col-12 p-0">
                    <div
                      v-if="countDocs > 0"
                      class="row mb-2">
                      <label
                        class="col-12 subtitle"> {{ $t('message.ticketOverview.attachment.document') }} ({{
                          countDocs
                        }})</label>
                      <template
                        v-for="media in getFilteredMediaListForView()">
                        <div
                          v-if="!(['image/jpeg', 'image/png', 'image/gif', 'video/webm', 'video/mp4'].indexOf(media.mimeType) !== -1)"
                          :key="media.id"
                          class="col-12 square">
                          <div
                            class="file-block">
                            <a
                              :href="media.url"
                              data-v-09485260=""
                              tabindex="0"
                              target="_blank">
                              {{ media.filename }}
                            </a>
                            <coozzy-button
                              class="delete-icon"
                              design="transparent"
                              data-cy="ticket-media-delete-btn"
                              @click="deleteMedia($event, media.id)">
                              <coozzy-delete-icon />
                            </coozzy-button>
                          </div>
                        </div>
                      </template>
                    </div>
                    <div
                      v-if="getRequestMediaListForView().length > 0"
                      class="row mb-2">
                      <label
                        class="col-12 subtitle"> {{ $t('message.ticketOverview.attachment.document') }}
                        ({{ getRequestMediaListForView().length }})</label>
                      <template
                        v-for="media in getRequestMediaListForView()">
                        <div
                          :key="`media-${media.id}`"
                          class="col-12 square">
                          <div
                            class="file-block">
                            <p
                              class="link m-0"
                              @click="downloadRequestsFile(media.blob, media.filename)">
                              {{ media.filename }}
                            </p>
                            <coozzy-button
                              class="delete-icon"
                              design="transparent"
                              data-cy="ticket-media-delete-btn"
                              @click="deleteRequestMedia($event, media.requestId)">
                              <coozzy-delete-icon />
                            </coozzy-button>
                          </div>
                        </div>
                      </template>
                    </div>
                    <div
                      v-if="countImg > 0"
                      class="row m-0 mb-2">
                      <label class="col-12 p-0 subtitle"> {{ $t('message.ticketOverview.attachment.image') }}
                        ({{ countImg }})</label>
                      <template
                        v-for="media in getFilteredMediaListForView()">
                        <div
                          v-if="['image/jpeg', 'image/png', 'image/gif'].indexOf(media.mimeType) !== -1"
                          :key="media.id"
                          class="media-block col-2 square">
                          <div
                            :style="{ 'background-image': 'url(' + media.url + ')' }"
                            class="uploaded-file w-100"
                            style="background-repeat: no-repeat;background-size: cover;background-position: center center;"
                            @click="imageClicked(media)">
                            <coozzy-button
                              class="delete-icon image"
                              design="transparent"
                              data-cy="ticket-media-delete-btn"
                              @click="deleteMedia($event, media.id)">
                              <coozzy-delete-icon />
                            </coozzy-button>
                          </div>
                        </div>
                      </template>
                    </div>
                    <div
                      v-if="countVideo > 0"
                      class="row m-0">
                      <label class="col-12 p-0 subtitle"> {{ $t('message.ticketOverview.attachment.video') }}
                        ({{ countVideo }})</label>
                      <template
                        v-for="media in getFilteredMediaListForView()">
                        <div
                          v-if="['video/webm', 'video/mp4'].indexOf(media.mimeType) !== -1"
                          :key="media.id"
                          class="media-block col-2 square">
                          <coozzy-thumbnail
                            v-if="getMediaUrl(media) !== null"
                            :key="media.id"
                            :video-src="getMediaUrl(media)"
                            :video-url="media.url"
                            class="uploaded-file cursor-pointer"
                            @imageClicked="openVideo(media)" />
                          <coozzy-button
                            class="delete-icon video"
                            design="transparent"
                            data-cy="ticket-media-delete-btn"
                            @click="deleteMedia($event, media.id)">
                            <coozzy-delete-icon />
                          </coozzy-button>
                        </div>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div
              id="list-connections"
              class="row mb-2 connection">
              <label
                class="col-12"
                for="connections"> {{ $t('message.ticketOverview.ticket.connections') }}</label>
              <div
                v-for="(ref, index) in references"
                :key="index"
                class="col-12 d-flex align-items-center">
                <coozzy-button
                  class="border mb-2"
                  design="transparent"
                  size="small"
                  data-cy="ticket-connection-remove-btn"
                  @click="removeReference(index)">
                  <coozzy-delete-icon />
                </coozzy-button>
                <div class="containerCircles">
                  <div
                    v-if="ref.tenancy"
                    v-b-tooltip="{container: '#list-connections', placement : 'left', title : $t('message.ticketOverview.ticket.connection_items.tenancy')}"
                    class="color-box mt-0 ml-2 mr-2 tenancy" />
                  <div
                    v-if="ref.condominium"
                    v-b-tooltip="{container: '#list-connections', placement : 'left', title : $t('message.ticketOverview.ticket.connection_items.condo_owner')}"
                    class="color-box mt-0 ml-2 mr-2 condo_owner" />
                  <div
                    v-if="ref.object && ref.object.condominiumOwnership === true && !ref.tenancy && !ref.condominium && !ref.device && !ref.request"
                    v-b-tooltip="{container: '#list-connections', placement : 'left', title : $t('message.ticketOverview.ticket.connection_items.condominium')}"
                    class="color-box mt-0 ml-2 mr-2 condominium" />
                  <div
                    v-if="ref.object && ref.object.condominiumOwnership === false && !ref.tenancy && !ref.condominium && !ref.device && !ref.request"
                    v-b-tooltip="{container: '#list-connections', placement : 'left', title : $t('message.ticketOverview.ticket.connection_items.object')}"
                    class="color-box mt-0 ml-2 mr-2 object" />
                  <div
                    v-if="ref.device"
                    v-b-tooltip="{container: '#list-connections',placement : 'left', title : $t('message.ticketOverview.ticket.connection_items.device')}"
                    class="color-box mt-0 ml-2 mr-2 device" />
                  <div
                    v-if="ref.mortgage"
                    v-b-tooltip="{container: '#collapse-connections',placement : 'left', title : $t('message.ticketOverview.ticket.connection_items.mortgage')}"
                    class="color-box mt-0 ml-2 mr-2 mortgage" />
                  <div
                    v-if="ref.insurance"
                    v-b-tooltip="{container: '#collapse-connections',placement : 'left', title : $t('message.ticketOverview.ticket.connection_items.insurance')}"
                    class="color-box mt-0 ml-2 mr-2 insurance" />
                  <div
                    v-if="ref.building && !ref.object && !ref.device && !ref.mortgage && !ref.insurance"
                    v-b-tooltip="{container: '#list-connections',placement : 'left', title : $t('message.ticketOverview.ticket.connection_items.building')}"
                    class="color-box mt-0 ml-2 mr-2 not_activated" />
                  <div
                    v-if="ref.property && !ref.building && !ref.device && !ref.mortgage && !ref.insurance"
                    v-b-tooltip="{container: '#list-connections',placement : 'left', title : $t('message.ticketOverview.ticket.connection_items.property')}"
                    class="color-box mt-0 ml-2 mr-2 current" />
                  <div
                    v-if="ref.contact"
                    v-b-tooltip="{container: '#list-connections',placement : 'left', title : $t('message.ticketOverview.ticket.connection_items.contact')}"
                    class="color-box mt-0 ml-2 mr-2 terminated" />
                  <div
                    v-if="ref.request"
                    v-b-tooltip="{container: '#list-connections',placement : 'left', title : $t('message.ticketOverview.ticket.connection_items.request')}"
                    class="color-box mt-0 ml-2 mr-2 request" />
                </div>
                <span v-if="ref.property">
                  <router-link
                    v-if="!isMarketingModule"
                    :to="redirectToPropertyDetail(ref.property.objectID || ref.property.id)">
                    {{ ref.property.name }}
                  </router-link>
                  <span v-else>{{ ref.property.name }}</span>
                </span>
                <span v-if="ref.building">
                  <span v-if="ref.property"> > </span>
                  <router-link
                    v-if="!isMarketingModule"
                    :to="redirectToBuildingDetail(ref.building.objectID || ref.building.id)">
                    {{ ref.building.name }}
                  </router-link>
                  <span v-else>{{ ref.building.name }}</span>
                </span>
                <span v-if="ref.object">
                  <span v-if="ref.building"> > </span>
                  <router-link
                    v-if="!isMarketingModule"
                    :to="redirectToDetail(ref.object.buildingId, ref.object.objectID || ref.object.id, 'objects')">
                    {{
                      getObjectConnectionLabel(ref.object)
                    }}
                  </router-link>
                  <span v-else> {{
                    getObjectConnectionLabel(ref.object)
                  }}</span>
                </span>

                <span v-if="ref.mortgage">
                  <template v-if="ref.property">
                    <span> > </span>
                    <span
                      v-if="!isMarketingModule"
                      @click="setMortgageId('property', ref.mortgage.id)"
                      @click.middle="setMortgageId('property', ref.mortgage.id)">
                      <router-link
                        v-if="!isMarketingModule"
                        :to="redirectToPropertyDetail(ref.property.id)">
                        {{
                          ref.mortgage.bankName + ' - ' + $t('message.onBoarding.mortgage.mortgageDesignation.' + ref.mortgage.mortgageDesignation)
                        }}
                      </router-link>
                    </span>
                    <span v-else>{{
                      ref.mortgage.bankName + ' - ' + $t('message.onBoarding.mortgage.mortgageDesignation.' + ref.mortgage.mortgageDesignation)
                    }}</span>
                  </template>
                  <template v-else-if="ref.building">
                    <span> > </span>
                    <span v-if="!isMarketingModule">
                      <router-link
                        :to="redirectToBuildingDetail(ref.building.id)">
                        {{
                          ref.mortgage.bankName + ' - ' + $t('message.onBoarding.mortgage.mortgageDesignation.' + ref.mortgage.mortgageDesignation)
                        }}
                      </router-link>
                    </span>
                    <span v-else>{{
                      ref.mortgage.bankName + ' - ' + $t('message.onBoarding.mortgage.mortgageDesignation.' + ref.mortgage.mortgageDesignation)
                    }}</span>
                  </template>
                </span>
                <span v-if="ref.insurance">
                  <template v-if="ref.building">
                    <span> > </span>
                    <span
                      v-if="!isMarketingModule"
                      @click="setInsuranceId('building', ref.insurance.id)"
                      @click.middle="setInsuranceId('building', ref.insurance.id)">
                      <router-link
                        v-if="!isMarketingModule"
                        :to="redirectToBuildingDetail(ref.building.id)">
                        {{ $t('message.onBoarding.buildings.insurances.type.' + ref.insurance.type) }}
                      </router-link>
                    </span>
                    <span v-else>{{ $t('message.onBoarding.buildings.insurances.type.' + ref.insurance.type) }}</span>
                  </template>
                  <template v-if="ref.property && !ref.building">
                    <span> > </span>
                    <span
                      v-if="!isMarketingModule"
                      @click="setInsuranceId('property', ref.insurance.id)"
                      @click.middle="setInsuranceId('property', ref.insurance.id)">
                      <router-link
                        v-if="!isMarketingModule"
                        :to="redirectToPropertyDetail(ref.property.id)">
                        {{ $t('message.onBoarding.buildings.insurances.type.' + ref.insurance.type) }}
                      </router-link>
                    </span>
                    <span v-else>{{ $t('message.onBoarding.buildings.insurances.type.' + ref.insurance.type) }}</span>
                  </template>
                </span>
                <span v-if="ref.room">
                  <span v-if="ref.object || ref.building || ref.property"> > </span>
                  <a
                    v-if="!isMarketingModule && canOpenRoomConnection(ref)"
                    class="external-link cursor-pointer"
                    href="#"
                    @click.prevent="openRoomConnection(ref)"
                    @mousedown.middle.prevent="openRoomConnection(ref, true)">
                    {{ ref.room.name }}
                  </a>
                  <span v-else>{{ ref.room.name }}</span>
                </span>
                <span v-if="ref.device">
                  <span v-if="ref.object || ref.building || ref.property"> > </span>
                  <router-link
                    v-if="redirectToDeviceDetail(ref.device.referenceId, ref.device.objectID || ref.device.id) && !isMarketingModule"
                    :to="redirectToDeviceDetail(ref.device.referenceId, ref.device.objectID || ref.device.id)">
                    <template v-if="ref.device.type && ref.device.type !== 'DEVICE_TYPE_UNDEFINED'">
                      {{
                        getDeviceTypeLabel(ref.device.type)
                      }}
                    </template>
                    <template v-else>
                      -
                    </template>
                  </router-link>
                  <span v-else>
                    <template v-if="ref.device.type && ref.device.type !== 'DEVICE_TYPE_UNDEFINED'">
                      {{
                        getDeviceTypeLabel(ref.device.type)
                      }}
                    </template>
                    <template v-else>
                      -
                    </template>
                  </span>
                </span>
                <span v-if="ref.object && ref.tenancy">
                  <span v-if="ref.object"> > </span>
                  <router-link
                    v-if="!isMarketingModule"
                    :to="redirectToDetail(ref.object.buildingId, ref.tenancy.objectID || ref.tenancy.id, 'tenants')">
                    <template v-if="ref.tenancy.tenants">
                      <span
                        v-for="(contact, j) in ref.tenancy.tenants"
                        :key="j">
                        <span v-if="j > 0">- </span>{{ contact.name }}
                      </span>
                    </template>
                    <template v-else>
                      <span
                        v-for="(contact, j) in ref.listContact"
                        :key="j">
                        <span v-if="j > 0">- </span>{{ contact.name }}
                      </span>
                    </template>
                  </router-link>
                  <span v-else>
                    <template v-if="ref.tenancy.tenants">
                      <span
                        v-for="(contact, j) in ref.tenancy.tenants"
                        :key="j">
                        <span v-if="j > 0">- </span>{{ contact.name }}
                      </span>
                    </template>
                    <template v-else>
                      <span
                        v-for="(contact, j) in ref.listContact"
                        :key="j">
                        <span v-if="j > 0">- </span>{{ contact.name }}
                      </span>
                    </template>
                  </span>
                </span>
                <span v-if="ref.object && ref.condominium">
                  <span v-if="ref.object"> > </span>
                  <router-link
                    v-if="!isMarketingModule"
                    :to="redirectToDetail(ref.object.buildingId, ref.condominium.objectID || ref.condominium.id, 'condominium')">
                    <template v-if="ref.condominium.condominiumOwners">
                      <span
                        v-for="(contact, j) in ref.condominium.condominiumOwners"
                        :key="j">
                        {{ contact.name }}
                      </span>
                    </template>
                    <template v-else>
                      <span
                        v-for="(contact, j) in ref.listContact"
                        :key="j">
                        {{ contact.name }}
                      </span>
                    </template>
                  </router-link>
                  <span v-else>
                    <template v-if="ref.condominium.condominiumOwners">
                      <span
                        v-for="(contact, j) in ref.condominium.condominiumOwners"
                        :key="j">
                        {{ contact.name }}
                      </span>
                    </template>
                    <template v-else>
                      <span
                        v-for="(contact, j) in ref.listContact"
                        :key="j">
                        {{ contact.name }}
                      </span>
                    </template>
                  </span>
                </span>
                <span v-if="ref.contact">
                  <router-link
                    v-if="!isMarketingModule"
                    :to="redirectToContactDetail(getContactRouteId(ref.contact))">
                    {{ ref.contact.name }}
                  </router-link>
                  <span v-else>{{ ref.contact.name }}</span>
                </span>
                <span v-if="ref.request">
                  <span> > </span>
                  <router-link
                    :to="redirectToRequestDetail(ref.request.objectId, ref.request.id)">
                    <span
                      v-for="(contact, j) in ref.listContact"
                      :key="j">
                      <span v-if="j > 0">- </span>{{ contact.name }}
                    </span>
                  </router-link>
                </span>
              </div>
              <div class="col-12">
                <div class="row">
                  <div class="col-3">
                    <coozzy-form-select
                      v-model="typeOfConnection"
                      data-cy="ticket-select-connection-type">
                      <option
                        :value="null">
                        {{ $t('message.generic.all') }}
                      </option>
                      <option
                        v-for="item in availableConnectionTypes"
                        :key="item.key"
                        :value="item">
                        {{ $t('message.ticketOverview.ticket.connection_items.' + item.key) }}
                      </option>
                    </coozzy-form-select>
                  </div>
                  <div class="col-9">
                    <contact-search-provider
                      :filters="connectionContactFilters"
                      :disabled="!includesContacts"
                      class="w-100"
                      silent>
                      <template #default="{ hits: contactHits, refine: refineContacts }">
                        <ais-instant-search
                          v-if="searchClient"
                          :index-name="algoliaIndexName"
                          :routing="routing"
                          :search-client="searchClient"
                          class="w-100">
                          <!-- eslint-disable vue/attribute-hyphenation -->
                          <ais-configure
                            :filters="query"
                            :hitsPerPage="10" />
                          <template v-if="typeOfConnection === null">
                            <ais-index index-name="building" />
                            <ais-index index-name="tenancy" />
                            <ais-index index-name="property" />
                            <ais-index index-name="condominium" />
                          </template>
                          <!-- eslint-disable vue/attribute-hyphenation -->
                          <ais-autocomplete
                            v-if="!indexNameChanging"
                            :key="refrecheAutosuggestKey">
                            <template #default="{ indices, refine }">
                              <vue-autosuggest
                                :key="refrecheAutosuggestKey"
                                ref="autoSuggest_connections"
                                v-model="emailSuggestion"
                                data-cy="ticket-input-search-connection"
                                :input-props="{...inputProps, placeholder: $t('message.generic.search')}"
                                :suggestions="indicesToSuggestions(indices, contactHits)"
                                @input="onChange(refine, refineContacts, emailSuggestion)"
                                @selected="onSelect">
                                <template #default="{ suggestion }">
                                  <span
                                    :id="'tooltip-target_' + suggestion.item.objectID">
                                    <template
                                      v-if="suggestion.item.objectID.startsWith('tenancy')">
                                      <div
                                        :class="checkSelectedElement(suggestion.item.objectID)"
                                        class="row m-0">
                                        <div
                                          class="p-0">
                                          <div
                                            v-b-tooltip="{placement : 'left', title : $t('message.ticketOverview.ticket.connection_items.tenancy')}"
                                            class="color-box ml-2 mr-2 tenancy" />
                                        </div>

                                        <div
                                          class="col p-0">
                                          <p
                                            v-if="suggestion.item.objectID.startsWith('tenancy')"
                                            class="mb-0">
                                            <strong v-if="suggestion.item.tenants.length > 0">
                                              <span
                                                v-for="(contact, j) in suggestion.item.tenants"
                                                :key="j">
                                                {{ contact.name }} <span
                                                  v-if="j < suggestion.item.tenants.length - 1">-</span>
                                              </span>
                                            </strong>
                                            <strong v-else>-</strong>
                                          </p>
                                          <p class="mb-0">
                                            {{ $t('message.onBoarding.buildings.objects.category.title') }}:
                                            {{ getConnectionObjectCategoryLabel(suggestion.item) }}
                                          </p>
                                          <p class="mb-0">
                                            {{ suggestion.item.startingAt | objectToDate }} -
                                            {{ suggestion.item.endingAt | objectToDate }}
                                          </p>
                                        </div>
                                        <span class="showSelected">
                                          {{ $t('message.ticketOverview.ticket.connection_items.selected') }}
                                        </span>
                                      </div>
                                    </template>
                                    <template
                                      v-if="suggestion.item.objectID.startsWith('condominium')">
                                      <div
                                        :class="checkSelectedElement(suggestion.item.objectID)"
                                        class="row m-0">
                                        <div
                                          class="p-0">
                                          <div
                                            v-b-tooltip="{placement : 'left', title : $t('message.ticketOverview.ticket.connection_items.condo_owner')}"
                                            class="color-box ml-2 mr-2 condo_owner" />
                                        </div>

                                        <div class="col p-0">
                                          <p
                                            v-if="suggestion.item.objectID.startsWith('condominium')"
                                            class="mb-0">
                                            <strong>
                                              {{
                                                suggestion.item.condominiumOwners.length > 0 ? suggestion.item.condominiumOwners[0].name : '-'
                                              }}
                                            </strong>
                                          </p>
                                          <p class="mb-0">
                                            {{ $t('message.onBoarding.buildings.objects.category.title') }}:
                                            {{ getConnectionObjectCategoryLabel(suggestion.item) }}
                                          </p>
                                          <p class="mb-0">
                                            {{ suggestion.item.startingAt | objectToDate }} -
                                            {{ suggestion.item.endingAt | objectToDate }}
                                          </p>
                                        </div>
                                        <span class="showSelected">
                                          {{ $t('message.ticketOverview.ticket.connection_items.selected') }}
                                        </span>
                                      </div>
                                    </template>
                                    <template
                                      v-else-if="suggestion.item.objectID.startsWith('obj')">
                                      <div
                                        :class="checkSelectedElement(suggestion.item.objectID)"
                                        class="row m-0">
                                        <div
                                          class="p-0">
                                          <div
                                            v-b-tooltip="{placement : 'left', title : $t('message.ticketOverview.ticket.connection_items.' + [suggestion.item.condominiumOwnership === true ? 'condominium' : 'object'])}"
                                            :class="[suggestion.item.condominiumOwnership === true ? 'condominium' : 'object']"
                                            class="color-box ml-2 mr-2" />
                                        </div>
                                        <div class="col p-0">
                                          <p class="mb-0">
                                            <b>{{ suggestion.item.name }}</b>
                                          </p>
                                          <p class="mb-0">
                                            <address-text
                                              :address="suggestion.item.address"
                                              :hide-if-empty="true"
                                              :show-short-country="true" />
                                          </p>
                                        </div>
                                        <span class="showSelected">
                                          {{ $t('message.ticketOverview.ticket.connection_items.selected') }}
                                        </span>
                                      </div>
                                    </template>
                                    <template
                                      v-else-if="suggestion.item.objectID.startsWith('prop')">
                                      <div
                                        :class="checkSelectedElement(suggestion.item.objectID)"
                                        class="row m-0">
                                        <div
                                          class="p-0">
                                          <div
                                            v-b-tooltip="{placement : 'left', title : $t('message.ticketOverview.ticket.connection_items.property')}"
                                            class="color-box ml-2 mr-2 current" />
                                        </div>
                                        <div class="col p-0">
                                          <p class="mb-0">
                                            <b>{{ suggestion.item.name }}</b>
                                          </p>
                                          <p class="mb-0">
                                            <address-text
                                              :address="suggestion.item.address"
                                              :hide-if-empty="true"
                                              :show-short-country="true" />
                                          </p>
                                        </div>
                                        <span class="showSelected">
                                          {{ $t('message.ticketOverview.ticket.connection_items.selected') }}
                                        </span>
                                      </div>
                                    </template>
                                    <template
                                      v-else-if="suggestion.item.objectID.startsWith('building')">
                                      <div
                                        :class="checkSelectedElement(suggestion.item.objectID)"
                                        class="row m-0">
                                        <div
                                          class="p-0">
                                          <div
                                            v-b-tooltip="{placement : 'left', title : $t('message.ticketOverview.ticket.connection_items.building')}"
                                            class="color-box ml-2 mr-2 not_activated" />
                                        </div>
                                        <div class="col p-0">
                                          <p class="mb-0">
                                            <b>{{ suggestion.item.name }}</b>
                                          </p>
                                          <p class="mb-0">
                                            <address-text
                                              :address="suggestion.item.address"
                                              :hide-if-empty="true"
                                              :show-short-country="true" />
                                          </p>
                                        </div>
                                        <span class="showSelected">
                                          {{ $t('message.ticketOverview.ticket.connection_items.selected') }}
                                        </span>
                                      </div>
                                    </template>
                                    <template v-else-if="suggestion.item.objectID.startsWith('contact')">
                                      <div
                                        :class="checkSelectedElement(suggestion.item.objectID)"
                                        class="row m-0">
                                        <div
                                          class="p-0">
                                          <div
                                            v-b-tooltip="{placement : 'left', title : $t('message.ticketOverview.ticket.connection_items.contact')}"
                                            class="color-box ml-2 mr-2 terminated" />
                                        </div>

                                        <div class="col p-0">
                                          <strong>{{ suggestion.item.lastName }} {{ suggestion.item.firstName }}</strong>
                                          <br>
                                          <address-text
                                            :address="suggestion.item.address"
                                            :hide-if-empty="true"
                                            :show-short-country="true" />
                                          <br>
                                          <span v-if="getListRole(suggestion.item).length > 0">
                                            {{ $t('message.generic.roles') }}: {{ getListRole(suggestion.item) }}
                                          </span>
                                        </div>
                                        <span class="showSelected">
                                          {{ $t('message.ticketOverview.ticket.connection_items.selected') }}
                                        </span>
                                      </div>
                                    </template>
                                    <b-tooltip
                                      v-if="checkSelectedElement(suggestion.item.objectID) === 'selected'"
                                      :container="'#list-connections'"
                                      :target="'tooltip-target_' + suggestion.item.objectID"
                                      placement="left"
                                      triggers="hover">
                                      {{ $t('message.generic.alreadySelected') }}
                                    </b-tooltip>
                                  </span>
                                </template>
                              </vue-autosuggest>
                            </template>
                          </ais-autocomplete>
                        </ais-instant-search>
                      </template>
                    </contact-search-provider>
                  </div>
                  <div
                    v-if="pendingConnection && requiresDeviceStep(pendingConnection)"
                    class="col-12 mt-2">
                    <div class="row">
                      <div class="col-12 mb-1">
                        <label class="mb-0">
                          {{ $t('message.ticketOverview.ticket.devicesOptional') }}
                        </label>
                      </div>
                      <div class="col-9">
                        <vue-autosuggest
                          :key="refrecheDeviceAutosuggestKey"
                          v-model="searchDeviceQuery"
                          data-cy="ticket-input-search-device"
                          :input-props="{ id: 'search_device_connection_creation', class: 'form-control w-100 connection-device-search-input', placeholder: $t('message.ticketOverview.ticket.searchDevice') }"
                          :suggestions="deviceIndicesToSuggestions()"
                          @selected="onSelectDevice">
                          <template #default="{ suggestion }">
                            <div
                              class="row m-0"
                              :class="[hasDeviceReference(suggestion.item) ? 'selected' : '', hasDeviceReference(suggestion.item) ? 'disableOptionHover' : '']">
                              <div class="p-0">
                                <div class="color-box ml-2 mr-2 device" />
                              </div>
                              <div class="col p-0">
                                <p class="mb-0 device-option-title">
                                  {{ formatDeviceMainLabel(suggestion.item) }}
                                </p>
                                <p class="mb-0 text-muted device-option-date">
                                  {{ formatDeviceDateLabel(suggestion.item) }}
                                </p>
                              </div>
                              <span class="showSelected">
                                {{ $t('message.ticketOverview.ticket.connection_items.selected') }}
                              </span>
                            </div>
                          </template>
                        </vue-autosuggest>
                      </div>
                      <div class="col-3">
                        <coozzy-button
                          class="w-100"
                          size="small"
                          design="green"
                          data-cy="ticket-connection-add-without-device-btn"
                          :disabled="isPendingRepeatableConnectionAlreadyAdded()"
                          @click="addPendingConnectionWithoutDevice">
                          {{ $t('message.ticketOverview.ticket.addWithoutDevice') }}
                        </coozzy-button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div
              v-if="linkedEmails.length > 0"
              id="linkedEmails"
              class="row mb-2">
              <label
                class="col-12"
                for="linkedEmails">
                {{ $t('message.ticketOverview.ticket.linkedEmail') }}
              </label>
              <div
                v-for="item in linkedEmails"
                :key="item.id"
                class="col-12 d-flex">
                <span
                  class="ml-4 cursor-pointer"
                  @click="showLinkedEmailDetail(item)">
                  <i>{{ item.timestamp | timestampToDateTime }}</i> - {{ item.subject || '-' }}
                </span>
                <coozzy-button
                  design="transparent"
                  class="pb-0 pt-0 pl-2"
                  data-cy="ticket-unlink-email-btn"
                  @click.stop="unlinkLinkedEmail(item)">
                  <coozzy-delete-icon />
                </coozzy-button>
              </div>
            </div>
            <div
              id="linkedTickets"
              class="row mb-2">
              <div class="col-12">
                <label for="linkedTickets">{{ $t('message.ticketOverview.ticket.linkTickets') }}</label>
                <div
                  v-if="isEditor || isTicketEditor"
                  class="row">
                  <div class="col-12 col-md-3 mb-2">
                    <coozzy-form-select
                      v-model="typeOfLink"
                      data-cy="ticket-select-link-type">
                      <option
                        v-for="item in availableLinkTypes"
                        :key="item"
                        :value="item">
                        {{ $t('message.ticketOverview.ticket.linkType.' + item) }}
                      </option>
                    </coozzy-form-select>
                  </div>
                  <div class="col-12 col-md-9">
                    <vue-autosuggest
                      :key="refrecheAutosuggestKey"
                      v-model="searchForTickets"
                      data-cy="ticket-select-link-search"
                      :get-suggestion-value="getSuggestionValue"
                      :input-props="{...inputProps, placeholder: $t('message.ticketOverview.ticket.search')}"
                      :suggestions="resultTicketsForLink"
                      @input="getTickets()"
                      @selected="onSelectLinked"
                      @item-changed="itemChanged">
                      <template #default="{suggestion}">
                        <span
                          :class="checkIsDisabled(suggestion.item) ? 'disableOptionHover' : ''"
                          class="my-suggestion-item">
                          <img
                            :src="`/img/ticket/${suggestion.item.type.split('TICKET_TYPE_')[1].toLowerCase()}.svg`"
                            alt="type"
                            class="mr-1 mb-1"
                            height="20px">
                          {{ suggestion.item.key }} {{ suggestion.item.title }}</span>
                      </template>
                    </vue-autosuggest>
                  </div>
                </div>
                <template v-if="filteredTicketLinked('TICKET_LINK_TYPE_BLOCKS').length > 0">
                  <div class="row">
                    <div class="col">
                      <label
                        class="subtitle"
                        for="blocks">
                        {{ $t('message.ticketOverview.ticket.linkType.TICKET_LINK_TYPE_BLOCKS') }}
                      </label>
                      <div
                        v-for="item in filteredTicketLinked('TICKET_LINK_TYPE_BLOCKS')"
                        :key="item.ticketId"
                        class="row mx-0 mb-2 p-1 linked-row">
                        <template v-if="listTickets.find(x => x.id === item.ticketId)">
                          <div class="col-8 p-0 label">
                            <img
                              v-b-tooltip="{placement : 'left', title : $t('message.ticketOverview.ticket.type.' + listTickets.find(x => x.id === item.ticketId).type)}"
                              :src="`/img/ticket/${listTickets.find(x => x.id === item.ticketId).type.split('TICKET_TYPE_')[1].toLowerCase()}.svg`"
                              alt="type"
                              class="mr-1"
                              height="20px"> {{ listTickets.find(x => x.id === item.ticketId).key }}
                            {{ listTickets.find(x => x.id === item.ticketId).title }}
                          </div>
                          <div class="col d-inline pr-1">
                            <coozzy-button
                              v-if="isEditor || isTicketEditor"
                              class="border float-right mb-0 ml-2"
                              design="transparent"
                              size="small"
                              data-cy="ticket-link-remove-btn"
                              @click="removeTicketLinked(item)">
                              <coozzy-delete-icon />
                            </coozzy-button>
                            <div
                              :class="`d-inline float-right status-design ${(listTickets.find(x => x.id === item.ticketId).status.split('TICKET_STATUS_')[1]).toLowerCase()}`">
                              {{
                                $t('message.ticketOverview.status.' + listTickets.find(x => x.id === item.ticketId).status)
                              }}
                            </div>
                            <img
                              v-b-tooltip="{container: '#linkedTickets', title: listTickets.find(x => x.id === item.ticketId).assignee ? listTickets.find(x => x.id === item.ticketId).assignee.name : ''}"
                              :src="listTickets.find(x => x.id === item.ticketId).assignee ? listTickets.find(x => x.id === item.ticketId).assignee.profilePictureUrl ? listTickets.find(x => x.id === item.ticketId).assignee.profilePictureUrl : '/avatar.png' : '/avatar.png'"
                              alt="assignee"
                              class="align-self-center float-right avatar mr-2">
                            <img
                              :src="`/img/priority/prio-${listTickets.find(x => x.id === item.ticketId).priority}.svg`"
                              alt="priority"
                              class="mr-2 float-right ml-2"
                              height="20px">
                          </div>
                        </template>
                      </div>
                    </div>
                  </div>
                </template>
                <template v-if="filteredTicketLinked('TICKET_LINK_TYPE_RELATES').length > 0">
                  <div class="row">
                    <div class="col">
                      <label
                        class="subtitle"
                        for="blocks">
                        {{ $t('message.ticketOverview.ticket.linkType.TICKET_LINK_TYPE_RELATES') }}
                      </label>
                      <div
                        v-for="item in filteredTicketLinked('TICKET_LINK_TYPE_RELATES')"
                        :key="item.ticketId"
                        class="row mx-0 mb-2 p-1 linked-row">
                        <template v-if="listTickets.find(x => x.id === item.ticketId)">
                          <div class="col-8 p-0 label">
                            <img
                              v-b-tooltip="{placement : 'left', title : $t('message.ticketOverview.ticket.type.' + listTickets.find(x => x.id === item.ticketId).type)}"
                              :src="`/img/ticket/${listTickets.find(x => x.id === item.ticketId).type.split('TICKET_TYPE_')[1].toLowerCase()}.svg`"
                              alt="type"
                              class="mr-1"
                              height="20px"> {{ listTickets.find(x => x.id === item.ticketId).key }}
                            {{ listTickets.find(x => x.id === item.ticketId).title }}
                          </div>
                          <div class="col d-inline pr-1">
                            <coozzy-button
                              v-if="isEditor || isTicketEditor"
                              class="border float-right mb-0 ml-2"
                              design="transparent"
                              size="small"
                              data-cy="ticket-link-remove-btn"
                              @click="removeTicketLinked(item)">
                              <coozzy-delete-icon />
                            </coozzy-button>
                            <div
                              :class="`d-inline float-right status-design ${(listTickets.find(x => x.id === item.ticketId).status.split('TICKET_STATUS_')[1]).toLowerCase()}`">
                              {{
                                $t('message.ticketOverview.status.' + listTickets.find(x => x.id === item.ticketId).status)
                              }}
                            </div>
                            <img
                              v-b-tooltip="{container: '#linkedTickets', title: listTickets.find(x => x.id === item.ticketId).assignee ? listTickets.find(x => x.id === item.ticketId).assignee.name : ''}"
                              :src="listTickets.find(x => x.id === item.ticketId).assignee ? listTickets.find(x => x.id === item.ticketId).assignee.profilePictureUrl ? listTickets.find(x => x.id === item.ticketId).assignee.profilePictureUrl : '/avatar.png' : '/avatar.png'"
                              alt="assignee"
                              class="align-self-center float-right avatar mr-2">
                            <img
                              :src="`/img/priority/prio-${listTickets.find(x => x.id === item.ticketId).priority}.svg`"
                              alt="priority"
                              class="mr-2 float-right ml-2"
                              height="20px">
                          </div>
                        </template>
                      </div>
                    </div>
                  </div>
                </template>
                <template v-if="filteredTicketLinked('TICKET_LINK_TYPE_IS_BLOCKED_BY').length > 0">
                  <div class="row">
                    <div class="col">
                      <label
                        class="subtitle"
                        for="blocks">
                        {{ $t('message.ticketOverview.ticket.linkType.TICKET_LINK_TYPE_IS_BLOCKED_BY') }}
                      </label>
                      <div
                        v-for="item in filteredTicketLinked('TICKET_LINK_TYPE_IS_BLOCKED_BY')"
                        :key="item.ticketId"
                        class="row mx-0 mb-2 p-1 linked-row">
                        <template v-if="listTickets.find(x => x.id === item.ticketId)">
                          <div class="col-8 p-0 label">
                            <img
                              v-b-tooltip="{placement : 'left', title : $t('message.ticketOverview.ticket.type.' + listTickets.find(x => x.id === item.ticketId).type)}"
                              :src="`/img/ticket/${listTickets.find(x => x.id === item.ticketId).type.split('TICKET_TYPE_')[1].toLowerCase()}.svg`"
                              alt="type"
                              class="mr-1"
                              height="20px"> {{ listTickets.find(x => x.id === item.ticketId).key }}
                            {{ listTickets.find(x => x.id === item.ticketId).title }}
                          </div>
                          <div class="col d-inline pr-1">
                            <coozzy-button
                              v-if="isEditor || isTicketEditor"
                              class="border float-right mb-0 ml-2"
                              design="transparent"
                              size="small"
                              data-cy="ticket-link-remove-btn"
                              @click="removeTicketLinked(item)">
                              <coozzy-delete-icon />
                            </coozzy-button>
                            <div
                              :class="`d-inline float-right status-design ${(listTickets.find(x => x.id === item.ticketId).status.split('TICKET_STATUS_')[1]).toLowerCase()}`">
                              {{
                                $t('message.ticketOverview.status.' + listTickets.find(x => x.id === item.ticketId).status)
                              }}
                            </div>
                            <img
                              v-b-tooltip="{container: '#linkedTickets', title: listTickets.find(x => x.id === item.ticketId).assignee ? listTickets.find(x => x.id === item.ticketId).assignee.name : ''}"
                              :src="listTickets.find(x => x.id === item.ticketId).assignee ? listTickets.find(x => x.id === item.ticketId).assignee.profilePictureUrl ? listTickets.find(x => x.id === item.ticketId).assignee.profilePictureUrl : '/avatar.png' : '/avatar.png'"
                              alt="assignee"
                              class="align-self-center float-right avatar mr-2">
                            <img
                              :src="`/img/priority/prio-${listTickets.find(x => x.id === item.ticketId).priority}.svg`"
                              alt="priority"
                              class="mr-2 float-right ml-2"
                              height="20px">
                          </div>
                        </template>
                      </div>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
          <div class="col-12 col-md-4">
            <div class="row mb-2">
              <div class="col">
                <label>{{ $t('message.ticketOverview.ticket.assignee') + '*' }}</label>
                <v-select
                  :key="listAllEmployees.length"
                  v-model="ticket.assignee"
                  data-cy="ticket-select-assignee"
                  :class="$v.ticket.assignee.$dirty && $v.ticket.assignee.$error ? 'is-invalid' : ''"
                  :filter="searchAssignee"
                  :options="listAllEmployees"
                  class="assignee">
                  <template #option="option">
                    <template v-if="option.value">
                      <img
                        :alt="option.label"
                        :src="option.value.profile.pictureUrl !== '' ? option.value.profile.pictureUrl : '/avatar.png'"
                        class="align-self-center avatar"
                        height="15px">
                      {{ option.label }} <b>{{ option.company ? '-' + option.company : '' }}</b>
                    </template>
                  </template>
                  <template #selected-option="option">
                    <template v-if="option.value">
                      <div>
                        <img
                          :alt="option.label"
                          :src="option.value.profile.pictureUrl !== '' ? option.value.profile.pictureUrl : '/avatar.png'"
                          class="align-self-center avatar"
                          height="15">
                        {{ option.label }} <b>{{ option.company ? '-' + option.company : '' }}</b>
                      </div>
                    </template>
                  </template>
                </v-select>
              </div>
            </div>
            <div class="row mb-2">
              <div
                v-if="ticket.type === 'TICKET_TYPE_ORDER'"
                class="col">
                <label>{{ $t('message.ticketOverview.ticket.operator') }}</label>
                <contact-search-select
                  v-model="correspondencesContactPerson"
                  data-cy="ticket-select-operator"
                  :filters="contactSearchFiltersOperator"
                  :transform-hits="filterHitsWithObjectId"
                  :clear-on-select="false"
                  :close-on-select="true"
                  :custom-label="customLabel"
                  :disabled="ticket.type === 'TICKET_TYPE_ORDER' && id !== '' && source !== 'changeOperator' && ticket.status !== 'TICKET_STATUS_DRAFT'"
                  :multiple="true"
                  :placeholder="''"
                  :preselect-first="false"
                  :preserve-search="true"
                  :taggable="false"
                  label="name"
                  track-by="objectID" />
                <label
                  v-if="listEmployeeCorrespondencesCP.length > 0"
                  class="sizeLabel">{{ $t('message.createOrder.contactPersonOfCompany') }} </label>
                <coozzy-form-select
                  v-if="listEmployeeCorrespondencesCP.length > 0"
                  v-model="correspondences"
                  data-cy="ticket-select-operator-contact"
                  :disabled="ticket.type === 'TICKET_TYPE_ORDER' && id !== '' && source !== 'changeOperator' && ticket.status !== 'TICKET_STATUS_DRAFT'"
                  class="select-entries float-right"
                  name="employeeCompany">
                  <option :value="null">
                    -
                  </option>
                  <option
                    v-for="(emp, index) in listEmployeeCorrespondencesCP"
                    :key="index"
                    :value="emp">
                    {{ emp.name }}
                  </option>
                </coozzy-form-select>
              </div>
            </div>
            <div
              v-if="ticket.type === 'TICKET_TYPE_ORDER'"
              class="row mb-2">
              <div
                class="col">
                <label>{{ $t('message.ticketOverview.ticket.contactPersonOnSite') }}</label>
                <contact-search-select
                  v-model="onSiteContactPersons"
                  data-cy="ticket-select-onsite-contact"
                  :filters="contactSearchFiltersOnSite"
                  :transform-hits="getSuggestion"
                  :clear-on-select="false"
                  :close-on-select="true"
                  :custom-label="customLabel"
                  :internal-search="false"
                  :multiple="true"
                  :placeholder="''"
                  :preselect-first="false"
                  :preserve-search="true"
                  :taggable="false"
                  label="name"
                  track-by="objectID" />
              </div>
            </div>
            <div class="row mb-2">
              <div class="col">
                <label>{{ $t('message.ticketOverview.ticket.priority.title') }}</label>
                <v-select
                  v-model="ticket.priority"
                  data-cy="ticket-select-priority"
                  :options="availablePriority"
                  label="title">
                  <template
                    #option="option">
                    <img
                      :alt="$tc('message.ticketOverview.ticket.priority.' + option.title)"
                      :src="`/img/priority/prio-${option.title}.svg`"
                      height="15px">
                    {{ $t('message.ticketOverview.ticket.priority.' + option.title) }}
                  </template>
                  <template
                    #selected-option="option">
                    <div>
                      <img
                        :alt="$tc('message.ticketOverview.ticket.priority.' + option.title)"
                        :src="`/img/priority/prio-${option.title}.svg`"
                        height="15">
                      {{ $t('message.ticketOverview.ticket.priority.' + option.title) }}
                    </div>
                  </template>
                </v-select>
              </div>
            </div>
            <div
              v-if="ticket.type !== 'TICKET_TYPE_OFFER'"
              class="row mb-2">
              <div class="col">
                <coozzy-form-input
                  v-model="dates.startDate"
                  data-cy="ticket-input-start-date"
                  :filter="'formatDate'"
                  :min="new Date().toISOString().slice(0, 10)"
                  :name="$tc('message.ticketOverview.ticket.orderBeExecutedFrom')"
                  type="date"
                  @blur="dateToObject()" />
              </div>
            </div>
            <div class="row mb-2">
              <div class="col">
                <coozzy-form-input
                  v-model="dates.dueDate"
                  data-cy="ticket-input-due-date"
                  :filter="'formatDate'"
                  :min="new Date().toISOString().slice(0, 10)"
                  :name="$tc('message.ticketOverview.ticket.dueDate') + (isDueDateRequired ? ' *' : '')"
                  :state="$v.ticket.dueDate.$dirty && $v.ticket.dueDate.$error ? false : null"
                  type="date"
                  @blur="dateToObject()" />
              </div>
            </div>
            <template
              v-if="ticket.costsEntries && ticket.costsEntries.length">
              <div class="row mb-1">
                <div class="col-12 col-md-4">
                  <label>{{ $t('message.ticketOverview.ticket.costs.room') }}</label>
                </div>
                <div class="col-12 col-md-4">
                  <label>{{ $t('message.ticketOverview.ticket.costs.costAbsorption') }}</label>
                </div>
                <div class="col-12 col-md-4">
                  <label>{{ $t('message.ticketOverview.ticket.costs.title') }}</label>
                </div>
              </div>
              <div
                v-for="(entry, index) in ticket.costsEntries"
                :key="index"
                class="row mb-2">
                <div class="col-12 col-md-4">
                  <p class="mb-0">
                    {{ entry.room }}
                  </p>
                </div>
                <div class="col-12 col-md-4">
                  <p class="mb-0">
                    {{ formatCostAbsorption(entry.costAbsorption) }}
                  </p>
                </div>
                <div class="col-12 col-md-4">
                  <p class="mb-0">
                    {{ formatCostShareValue(entry) }}
                  </p>
                </div>
              </div>
            </template>
            <div
              v-else-if="ticket.costs && ticket.costs.costShareValue"
              class="row mb-2">
              <div class="col-6">
                <coozzy-form-input
                  v-model="costAbsorption"
                  data-cy="ticket-input-costs"
                  :disabled="true"
                  :name="$tc('message.ticketOverview.ticket.costs.costAbsorption')"
                  type="text" />
              </div>
              <div class="col-6">
                <coozzy-form-input
                  v-model="costShareUnit"
                  data-cy="ticket-input-costs-unit"
                  :disabled="true"
                  :name="$tc('message.ticketOverview.ticket.costs.title')"
                  type="text" />
              </div>
            </div>
          </div>
        </div>
      </coozzy-card>
      <div class="row">
        <div
          v-if="!showTicketPreview"
          class="col-6 col-md-2">
          <coozzy-button
            :disabled="disableBtns || uploading"
            class="my-2 w-100 wizard-btn"
            data-cy="ticket-cancel-btn"
            @click="cancelClicked">
            {{ $t('message.generic.cancel') }}
          </coozzy-button>
        </div>
        <div
          v-if="ticket.type !== 'TICKET_TYPE_TASK' && (id === '' || ticket.status === 'TICKET_STATUS_DRAFT') && showTicketPreview"
          class="col-6 col-md-2">
          <coozzy-button
            :disabled="disableBtns || uploading"
            class="my-2 w-100 wizard-btn"
            data-cy="ticket-modify-btn"
            @click="toggleListTickets">
            {{ $t('message.generic.form.modify') }}
          </coozzy-button>
        </div>
        <div
          v-if="id !== '' && ticket.status === 'TICKET_STATUS_DRAFT'"
          :class="ticket.status === 'TICKET_STATUS_DRAFT' ? 'offset-md-4' : ''"
          class="col-6 col-md-2">
          <coozzy-button
            :disabled="disableBtns || uploading"
            class="my-2 w-100"
            design="red"
            data-cy="ticket-delete-btn"
            @click="deleteTicketClicked">
            {{ $t('message.generic.delete') }}
          </coozzy-button>
        </div>
        <div
          :class="ticket.status !== 'TICKET_STATUS_DRAFT' ? 'offset-md-6' : ''"
          class="col-6 col-md-2">
          <coozzy-button
            v-if="ticket.status === 'TICKET_STATUS_DRAFT'"
            :disabled="disableBtns || uploading"
            class="my-2 w-100"
            design="prop-green"
            data-cy="ticket-save-draft-btn"
            @click="saveTicket(true)">
            {{ $t('message.marketingObjects.editAdvert.saveDraft') }}
          </coozzy-button>
        </div>
        <div
          v-if="ticket.type !== 'TICKET_TYPE_TASK' && (id === '' || ticket.status === 'TICKET_STATUS_DRAFT') && !showTicketPreview"
          class="col-6 col-md-2">
          <coozzy-button
            :disabled="disableBtns || uploading"
            class="my-2 w-100"
            design="prop-green"
            data-cy="ticket-preview-btn"
            @click="toggleListTickets">
            {{ $t('message.ticketPreview') }}
          </coozzy-button>
        </div>
        <div
          v-if="id !== '' && ticket.status !== 'TICKET_STATUS_DRAFT' || (ticket.type !== 'TICKET_TYPE_TASK' && showTicketPreview) || ticket.type === 'TICKET_TYPE_TASK'"
          class="col-6 col-md-2">
          <coozzy-button
            :disabled="disableBtns || uploading"
            class="my-2 w-100"
            design="prop-green"
            data-cy="ticket-save-btn"
            @click="saveTicket(false)">
            {{
              ticket.type === 'TICKET_TYPE_TASK' && !ticket.id ? $t('message.generic.create') : ticket.type === 'TICKET_TYPE_ORDER' && !ticket.id || ticket.id === '' || ticket.type === 'TICKET_TYPE_ORDER' && ticket.status === 'TICKET_STATUS_DRAFT' ? $t('message.generic.form.send') : $t('message.generic.form.save')
            }}
          </coozzy-button>
        </div>
      </div>
    </div>
    <b-modal
      id="confirmationModal"
      ref="confirmationModal"
      data-cy="ticket-confirmation-modal"
      :title="$t('message.onBoardingTabs.confirmModal.confirmationTitle')"
      hide-footer
      no-close-on-backdrop>
      <p>{{ $t('message.onBoardingTabs.confirmModal.confirmationText') }}</p>
      <div class="col">
        <coozzy-button
          class="border mb-0"
          design="transparent"
          size="small"
          data-cy="ticket-confirmation-cancel-btn"
          @click="$bvModal.hide('confirmationModal')">
          {{ $t('message.generic.cancel') }}
        </coozzy-button>
        <coozzy-button
          class="float-right mb-0"
          design="green"
          size="small"
          data-cy="ticket-confirmation-confirm-btn"
          @click="confirmCancellation">
          {{ $t('message.onBoardingTabs.confirmModal.confirmationButton') }}
        </coozzy-button>
      </div>
    </b-modal>
    <b-modal
      v-if="showDeleteTicketModal"
      id="deleteConfirmationModal"
      ref="deleteConfirmationModal"
      data-cy="ticket-delete-confirmation-modal"
      :title="$t('message.ticketOverview.deleteTicketModal.title')"
      hide-footer
      no-close-on-backdrop>
      <p>{{ $t('message.ticketOverview.deleteTicketModal.text') }}</p>
      <div class="col">
        <coozzy-button
          class="border mb-0"
          design="transparent"
          size="small"
          data-cy="ticket-delete-cancel-btn"
          @click="hideDeleteConfirmationModal">
          {{ $t('message.generic.cancel') }}
        </coozzy-button>
        <coozzy-button
          class="float-right mb-0"
          design="green"
          size="small"
          data-cy="ticket-delete-confirm-btn"
          @click="confirmDeletion">
          {{ $t('message.onBoardingTabs.confirmModal.confirmationButton') }}
        </coozzy-button>
      </div>
    </b-modal>
    <b-modal
      v-if="selectedObjectVideo !== ''"
      ref="show-video"
      data-cy="ticket-video-modal"
      hide-footer
      hide-header>
      <div class="row mb-2">
        <div class="col">
          <b-embed
            controls
            type="video">
            <source
              :src="selectedObjectVideo"
              type="video/webm">
            <source
              :src="selectedObjectVideo"
              type="video/mp4">
          </b-embed>
        </div>
      </div>
    </b-modal>
    <b-modal
      v-if="showInformOperatorModal"
      id="informOperatorModal"
      ref="informOperatorModal"
      data-cy="ticket-inform-operator-modal"
      :title="$t('message.ticketOverview.informOperatorModal.title')"
      hide-footer
      no-close-on-backdrop>
      <p>{{ $t('message.ticketOverview.informOperatorModal.description') }}</p>
      <div class="col">
        <coozzy-button
          class="border mb-0"
          design="transparent"
          size="small"
          data-cy="ticket-inform-operator-skip-btn"
          @click="proceedSave(true)">
          {{ $t('message.ticketOverview.informOperatorModal.doNotInform') }}
        </coozzy-button>
        <coozzy-button
          class="float-right mb-0"
          design="green"
          size="small"
          data-cy="ticket-inform-operator-confirm-btn"
          @click="proceedSave(false)">
          {{ $t('message.ticketOverview.informOperatorModal.inform') }}
        </coozzy-button>
      </div>
    </b-modal>
  </section>
</template>

<script lang="ts">
import ObjectApi from '@/misc/apis/ObjectApi'
import ContactApi from '@/misc/apis/ContactApi'
import { required, requiredIf } from 'vuelidate/lib/validators'
import AddressText from '@/framework/components/misc/AddressText.vue'
import Vue from 'vue'
import { algolia } from '@/mixins/algolia'
import TicketApi from '@/misc/apis/TicketApi'
import UserApi from '@/misc/apis/UserApi'
import RequestApi from '@/marketingBasic/api/RequestApi'
import ShareApi from '@/misc/apis/ShareApi'
import ContactSearchSelect from '@/framework/components/search/ContactSearchSelect.vue'
import ContactSearchProvider from '@/framework/components/search/ContactSearchProvider.vue'
import CoozzyFormSelect from '@/framework/components/form/select/CoozzyFormSelect.vue'
import CoozzyFormInput from '@/framework/components/form/input/CoozzyFormInput.vue'
import CoozzyButton from '@/framework/components/button/CoozzyButton.vue'
import CoozzySpinner from '@/framework/components/misc/CoozzySpinner.vue'
import { user } from '@/mixins/user'
import { validation } from '@/mixins/validation'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import { dateUtils } from '@/mixins/dateUtils'
import { VueAutosuggest } from 'vue-autosuggest'
import { routeChecks } from '@/mixins/routeChecks'
import MediaApi from '@/misc/apis/MediaApi'
import CoozzyCard from '@/framework/components/card/CoozzyCard.vue'
import CoozzyFormFileInput from '@/framework/components/form/fileInput/CoozzyFormFileInput.vue'
import CoozzyDeleteIcon from '@/framework/components/icons/CoozzyDeleteIcon.vue'
import { media } from '@/mixins/media'
import { formatting } from '@/mixins/formatting'
import { ticketConnections } from '@/mixins/ticketConnections'
import { buildDeviceDetailQuery } from '@/misc/utils/deviceNavigation'
import TicketDetailsView from '@/management/components/ticket/TicketDetailsView.vue'
import CoozzyThumbnail from '@/framework/components/img/CoozzyThumbnail.vue'
import MessageApi from '@/misc/apis/MessageApi'
import CoozzyInfoCircleIcon from '@/framework/components/icons/CoozzyInfoCircleIcon.vue'
import RoomApi from '@/misc/apis/RoomApi'
import CoozzyFormTextarea from '@/framework/components/form/textarea/CoozzyFormTextarea.vue'
import CoozzyEditIcon from '@/framework/components/icons/CoozzyEditIcon.vue'
import CoozzyFormTag from '@/framework/components/form/input/CoozzyFormTag.vue'
import CoozzySaveIcon from '@/framework/components/icons/CoozzySaveIcon.vue'
import draggable from 'vuedraggable'
import CoozzyCommentIconImg from '@/framework/components/icons/CoozzyCommentIconImg.vue'
import CoozzyEnterIcon from '@/framework/components/icons/CoozzyEnterIcon.vue'
import { Templatev2Template, TicketChecklistTemplate, TicketTicket, TicketTicketType } from '@/types/api'
import { Contact, EmployeeOption, TicketEmployee, TicketLinkView, TicketLinkedMailRow, TicketMediaView, TicketOwnerAccount, TicketReferenceView, TicketTaskView, TicketTemplateOption, TicketUiState } from '@/types/models'

type RequestMediaItem = {
  id?: string
  requestId: string
  filename: string
  blob: Blob
}

function greaterThanStartDate(value) {
  const dueDateInput = this.$options.filters.objectToDateInput(value)
  const startDateInput = this.$options.filters.objectToDateInput(this.ticket.startDate)
  // Treat null / empty / proto sentinels (year 0 or -1) as unset — objectToDateInput returns null/''
  if (!dueDateInput || !startDateInput) {
    return true
  }

  return new Date(dueDateInput) >= new Date(startDateInput)
}

type PendingLinkedEmailUnlink = {
  emailId: string
  referenceIds: string[]
}

type LinkedEmailQueryData = {
  id?: string
  referenceIds?: string[]
}

export default Vue.extend({
  name: 'TicketCreation',
  components: {
    CoozzyEnterIcon,
    CoozzyCommentIconImg,
    draggable,
    CoozzySaveIcon,
    CoozzyFormTag,
    CoozzyEditIcon,
    CoozzyFormTextarea,
    CoozzyInfoCircleIcon,
    CoozzyThumbnail,
    CoozzyDeleteIcon,
    CoozzyFormFileInput,
    CoozzyCard,
    CoozzyFormInput,
    ContactSearchSelect,
    ContactSearchProvider,
    CoozzySpinner,
    AddressText,
    VueAutosuggest,
    CoozzyButton,
    CoozzyFormSelect,
    TicketDetailsView
  },
  mixins: [user, validation, algolia, routeChecks, dateUtils, media, formatting, ticketConnections],
  metaInfo() {
    return {
      title: this.pageTitle
    }
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      ;(vm as Vue & { prevRoute: unknown }).prevRoute = from
    })
  },
  beforeRouteLeave(to, from, next) {
    this.nextToPath = to
    this.nextPath = next
    if (this.hasChanged) {
      this.$refs.confirmationModal.show()
    } else {
      this.nextPath()
    }
  },
  props: {
    id: {
      type: String,
      default: ''
    },
    fromBulk: {
      type: Boolean,
      default: false
    },
    ids: {
      type: [Array, String],
      default: () => {
        return []
      }
    },
    defaultAssignee: {
      type: [String, Number],
      default: ''
    },
    ticketType: {
      type: String,
      default: 'TICKET_TYPE_TASK'
    },
    ticketPriority: {
      type: String,
      default: ''
    },
    requestsData: { // => { title, listDocsPerRequest: [{documents, contacts, requestId}], objectOwner }
      type: String,
      default: ''
    },
    emailsData: { // => { email, media }
      type: String,
      default: ''
    },
    source: { // => changeOperator
      type: String,
      default: ''
    },
    buildingIds: {
      type: String,
      default: ''
    },
    propertyIds: {
      type: String,
      default: ''
    },
    objectIds: {
      type: String,
      default: ''
    },
    tenancyIds: {
      type: String,
      default: ''
    },
    condoOwnerIds: {
      type: String,
      default: ''
    },
    contactIds: {
      type: String,
      default: ''
    },
    deviceIds: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      CustomDeselect: {
        functional: true,
        render(h, context) {
          const title = context.parent.$t ? context.parent.$t('message.ticketOverview.ticket.removeAssignee') : 'Clear selection'
          return h(
            'span',
            {
              attrs: { title },
              class: 'vs__deselect',
              on: {
                mousedown: (e) => {
                  e.preventDefault()
                  if (context.listeners && context.listeners.deselect) {
                    context.listeners.deselect(e)
                  }
                }
              }
            },
            [
              h(
              'svg',
              {
                attrs: {
                  width: '10',
                  height: '10',
                  xmlns: 'http://www.w3.org/2000/svg'
                }
              },
              [
                h('path', {
                  attrs: {
                    d: 'M6.895455 5l2.842897-2.842898c.348864-.348863.348864-.914488 0-1.263636L9.106534.261648c-.348864-.348864-.914489-.348864-1.263636 0L5 3.104545 2.157102.261648c-.348863-.348864-.914488-.348864-1.263636 0L.261648.893466c-.348864.348864-.348864.914489 0 1.263636L3.104545 5 .261648 7.842898c-.348864.348863-.348864.914488 0 1.263636l.631818.631818c.348864.348864.914773.348864 1.263636 0L5 6.895455l2.842898 2.842897c.348863.348864.914772.348864 1.263636 0l.631818-.631818c.348864-.348864.348864-.914489 0-1.263636L6.895455 5z'
                  }
                })
              ]
            )
          ]
          )
        }
      },
      selectedImageMedia: null as TicketMediaView | null,
      refrecheAutosuggestKey: '',
      refrecheDeviceAutosuggestKey: '',
      debounceTimeout: null as ReturnType<typeof setTimeout> | null,
      galleryImages: [] as TicketMediaView[],
      emailSuggestion: '',
      searchDeviceQuery: '',
      ticketToLink: '',
      typeOfLink: 'TICKET_LINK_TYPE_RELATES',
      references: [] as TicketReferenceView[],
      pendingConnection: null,
      pendingConnectionDevices: [] as TicketReferenceView[],
      dates: {
        startDate: null,
        dueDate: null
      },
      // Snapshot from loaded ticket — legacy orders without dueDate stay optional on edit (COOZ-15935)
      hadDueDateOnLoad: false,
      loading: false,
      hasChanged: false,
      attachment: [] as File[],
      isLoaded: false,
      showLinkInputs: false,
      availableLinkTypes: ['TICKET_LINK_TYPE_RELATES', 'TICKET_LINK_TYPE_BLOCKS', 'TICKET_LINK_TYPE_IS_BLOCKED_BY'],
      availablePriority: ['LOW', 'DEFAULT', 'HIGH'],
      mediasList: [] as TicketMediaView[],
      uploading: false,
      ticketsForLink: [] as TicketTicket[],
      listTickets: [] as TicketTicket[],
      currentEmployeeList: [] as TicketEmployee[],
      listAllEmployees: [] as EmployeeOption[],
      ownerAccounts: [] as TicketOwnerAccount[],
      searchForTickets: '',
      ticket: {
        accountId: '',
        source: 'UNDEFINED_SOURCE',
        type: 'TICKET_TYPE_TASK',
        status: 'TICKET_STATUS_OPEN',
        title: '',
        description: '',
        mediaIds: [],
        ownerId: '',
        priority: 'DEFAULT',
        startDate: null,
        dueDate: null,
        assignee: null,
        onSiteContact: null,
        onSiteContacts: [],
        watchers: [],
        implementationLead: {},
        correspondences: [],
        externalTicketWatcher: [],
        links: [],
        references: [],
        involvedPersons: [],
        checklist: [],
        costs: {
          costAbsorption: 'COST_ABSORPTION_UNDEFINED',
          costShareUnit: 'COST_SHARE_UNIT_UNDEFINED',
          costShareValue: 0,
          room: ''
        }
      } as TicketUiState,
      optedPortionKeyValue: ['OPTED_PORTION_KEY_M2', 'OPTED_PORTION_KEY_M3', 'OPTED_PORTION_KEY_VALUE_RATIO', 'OPTED_PORTION_KEY_TV', 'OPTED_PORTION_KEY_ELEVATOR', 'OPTED_PORTION_KEY_LOCATION_COMPENSATION'],
      typeValue: [] as string[],
      isAddressInvalid: false,
      awaitingSearch: false,
      selectedObjectVideo: '',
      countDocs: 0,
      countImg: 0,
      countVideo: 0,
      prevRoute: null,
      nextToPath: null,
      nextPath: null,
      listEntities: [] as string[],
      showTicketPreview: false,
      ticketPreview: {
        key: '',
        accountId: '',
        source: 'UNDEFINED_SOURCE',
        type: 'TICKET_TYPE_TASK',
        status: 'TICKET_STATUS_OPEN',
        title: '',
        description: '',
        mediaIds: [],
        ownerId: this.$store.getters['user/getAccountId'],
        priority: 'DEFAULT',
        startDate: null,
        dueDate: null,
        assignee: null,
        onSiteContact: null,
        onSiteContacts: [],
        watchers: [],
        implementationLead: {},
        correspondences: [],
        externalTicketWatcher: [],
        links: [],
        references: [],
        involvedPersons: [],
        checklist: [],
        costs: {
          costAbsorption: 'COST_ABSORPTION_UNDEFINED',
          costShareUnit: 'COST_SHARE_UNIT_UNDEFINED',
          costShareValue: 0,
          room: ''
        }
      } as TicketUiState,
      disableBtns: false,
      onSiteContactPersons: null as Contact[] | null,
      correspondences: null as Contact | null,
      correspondencesContactPerson: null as Contact[] | null,
      listEmployeeOnSiteCP: [] as Contact[],
      listEmployeeCorrespondencesCP: [] as Contact[],
      typeOfConnection: null,
      availableConnectionTypes: [
        {
          key: 'property',
          value: 'property'
        },
        {
          key: 'building',
          value: 'building'
        },
        {
          key: 'object',
          value: 'object'
        },
        {
          key: 'condominium',
          value: 'object'
        },
        {
          key: 'tenancy',
          value: 'tenancy'
        },
        {
          key: 'condo_owner',
          value: 'condominium'
        },
        {
          key: 'contact',
          value: 'contact'
        }
      ],
      query: '',
      selectedTemplate: '',
      indexNameChanging: false,
      assignee: null as Contact | null,
      listRequestsMedia: [] as RequestMediaItem[],
      listEmployeeContact: [] as Contact[],
      generatedDescription: '',
      linksUpdated: false,
      listLinks: [] as TicketLinkView[],
      linksInit: false,
      showDeleteTicketModal: false,
      showInformOperatorModal: false,
      pendingDraft: false,
      pendingUpdateMask: null as string | null,
      initialTasks: [] as string[],
      listTasks: [] as TicketTaskView[],
      editingTask: null,
      selectedTask: null,
      availableStatusChecklist: ['CHECKLIST_ITEM_STATUS_OPEN', 'CHECKLIST_ITEM_STATUS_IN_PROGRESS', 'CHECKLIST_ITEM_STATUS_DONE'],
      editingTaskNote: null,
      listChecklistTemplates: [] as TicketChecklistTemplate[],
      linkedEmails: [] as TicketLinkedMailRow[],
      emailsDataForLinking: null as LinkedEmailQueryData | null,
      pendingLinkedEmailUnlinks: [] as PendingLinkedEmailUnlink[]
    }
  },
  computed: {
    ...mapState('message', ['templates', 'mailboxEntries']),
    costAbsorption: {
      get() {
        return this.ticket.costs.costAbsorption !== 'COST_ABSORPTION_UNDEFINED' ? this.$t('message.ticketOverview.ticket.costs.costAbsorptionValues.' + this.ticket.costs.costAbsorption) : ''
      },
      set(value) {
        Vue.set(this.ticket.costs, 'costAbsorption', value)
      }
    },
    costShareUnit: {
      get() {
        if (this.ticket.costs.costShareUnit === 'COST_SHARE_UNIT_UNDEFINED') {
          return ''
        } else {
          if (this.ticket.costs.costShareUnit === 'COST_SHARE_UNIT_PERCENT') {
            return this.ticket.costs.costShareValue + '%'
          } else {
            return `${this.$options.filters.formatPrice(this.ticket.costs.costShareValue, 'CHF')}`
          }
        }
      },
      set(value) {
        Vue.set(this.ticket.costs, 'costShareUnit', value)
      }
    },
    sortedTemplate(): TicketTemplateOption[] {
      const templates: TicketTemplateOption[] = this.ticket.type === 'TICKET_TYPE_CHECKLIST'
        ? this.listChecklistTemplates
        : (this.templates as Templatev2Template[]).filter(x => x.type === 'TEMPLATE_TYPE_TICKET')
      return templates.slice().sort((a, b) => {
        if (a.name > b.name) {
          return 1
        }
        if (b.name > a.name) {
          return -1
        }
        return 0
      })
    },
    contactSearchFiltersOperator() {
      return { isOwner: false, active: true, isEmployee: false, isOnlyInterestedParty: false }
    },
    contactSearchFiltersOnSite() {
      return { isOnlyInterestedParty: false, active: true }
    },
    /** Contacts come from the search read model, so the Algolia client never queries that index. */
    contactsOnly() {
      return this.typeOfConnection?.value === 'contact'
    },
    /** Required for new orders, and for edits that already had a dueDate when loaded. */
    isDueDateRequired(): boolean {
      return this.ticket?.type === 'TICKET_TYPE_ORDER' && (!this.id || this.hadDueDateOnLoad)
    },
    includesContacts() {
      return this.typeOfConnection === null || this.contactsOnly
    },
    algoliaIndexName() {
      return this.typeOfConnection && !this.contactsOnly ? this.typeOfConnection.value : 'object'
    },
    connectionContactFilters() {
      return { isOwner: false, isEmployee: false, isOnlyInterestedParty: false }
    },
    prevRoutePath() {
      return this.prevRoute ? this.prevRoute.path : '/'
    },
    prevRouteQuery() {
      return this.prevRoute ? this.prevRoute.query : null
    },
    filteredMediaList(): TicketMediaView[] {
      const array = this.mediasList
      array.sort((val1, val2) => {
        return parseInt(val2.uploadedAt) - parseInt(val1.uploadedAt)
      })
      if (array.length > 0) {
        array.forEach(element => {
          if (!(['image/jpeg', 'image/png', 'image/gif', 'video/webm', 'video/mp4'].indexOf(element.mimeType) !== -1)) {
            this.countDocs = this.countDocs + 1
          }
          if (['image/jpeg', 'image/png', 'image/gif'].indexOf(element.mimeType) !== -1) {
            this.countImg = this.countImg + 1
          }
          if (['video/webm', 'video/mp4'].indexOf(element.mimeType) !== -1) {
            this.countVideo = this.countVideo + 1
          }
        })
      }
      return array
    },
    pageTitle() {
      if (this.id && this.ticket) {
        return `${this.$t('message.ticketOverview.editTicket')}: ${this.ticket.key} - ${this.ticket.title}`
      } else {
        return this.$t('message.ticketOverview.createTicket')
      }
    },
    availableTypes() {
      return [
        TicketTicketType.TICKET_TYPE_TASK,
        ...(!this.isOwnerModule && !this.isAssetModule ? [TicketTicketType.TICKET_TYPE_ORDER] : []),
        TicketTicketType.TICKET_TYPE_CHECKLIST,
        ...(this.id && this.ticket ? [TicketTicketType.TICKET_TYPE_CLAIM] : [])
      ]
    },
    employeeList() {
      if (this.getEmployees().length > 0) {
        return this.getEmployees().filter(x => !x.blocked)
      }
      return []
    },
    avatar() {
      const profile = this.$store.getters['user/getCurrentUser']
      if (profile) {
        if (profile.picture.includes('gravatar')) {
          return '/avatar.png'
        }
        return profile.picture
      } else {
        return '/avatar.png'
      }
    },
    resultTicketsForLink() {
      return [{ data: this.ticketsForLink.filter(x => x && x.key && x.key !== this.ticket.key) }]
    }
  },
  watch: {
    selectedTemplate: {
      deep: true,
      handler: async function (val) {
        if (this.ticket.type === 'TICKET_TYPE_CHECKLIST') {
          await this.applyCheckListTemplate(val)
        } else {
          const template = this.sortedTemplate.find(x => x.id === val)
          if (!template) {
            return
          }
          let templateDescription = template.message
          if (template.message.includes('#anrede#') || template.message.includes('#eigentuemer#') || template.message.includes('#eigentuemer.addresse#')) {
            templateDescription = await this.translatePlaceholders(template.message)
          }
          this.ticket.description = templateDescription
        }
      }
    },
    correspondences: function (newVal) {
      if ((newVal === '' || newVal === null) && (this.correspondencesContactPerson?.length || 0) > 0) {
        this.ticket.correspondences = this.correspondencesContactPerson
      } else {
        this.ticket.correspondences = [newVal]
      }
    },
    correspondencesContactPerson: function (value) {
      if (value.length > 1) {
        this.correspondencesContactPerson = [value[value.length - 1]]
      }
      if (value.length > 0) {
        const item = value.length > 1 ? value[value.length - 1] : value.length > 0 ? value[0] : null
        if (item && !('contactPersons' in item)) {
          this.ticket.correspondences = [item]
          this.listEmployeeCorrespondencesCP = []
        } else {
          if (item && item.contactPersons.length > 0) {
            this.ticket.correspondences = [item]
            this.listEmployeeCorrespondencesCP = []
            const list = item.contactPersons
            this.correspondences = null
            list.forEach(element => {
              const emp = element
              emp.companyName = item.name
              emp.address = item.address
              if (emp.email === '') {
                emp.email = item.email
              }
              if (emp.phoneNumbers.length === 0) {
                emp.phoneNumbers = item.phoneNumbers
              }
              this.listEmployeeCorrespondencesCP.push(emp)
            })
          } else {
            this.ticket.correspondences = [item]
            this.listEmployeeCorrespondencesCP = []
          }
        }
      } else {
        this.correspondences = null
        this.listEmployeeCorrespondencesCP = []
        this.ticket.correspondences = []
      }
    },
    ticket: {
      deep: true,
      handler: function () {
        this.hasChanged = true
      }
    },
    mediaUpload: function (newVal) {
      if (newVal !== null) {
        this.sendFile()
      }
    },
    listAllEmployees: function (newVal) {
      if (newVal.length > 0) {
        if (this.ticket && this.ticket.assignee && this.ticket.assignee.name && !this.isLoaded) {
          this.ticket.assignee = newVal.find(x => x.value.id === this.ticket.assignee.userId || x.value.id === this.ticket.assignee.value.id)
          this.isLoaded = true
          this.$nextTick(function () {
            this.hasChanged = false
          })
        } else if (!this.ticket.assignee) {
          this.ticket.assignee = newVal.find(x => x.value.id === this.$store.getters['user/getUserId']) || null
        } else {
          if (!newVal.find(x => x.value.id === this.ticket.assignee.userId || x.value.id === this.ticket.assignee?.value?.id)) {
            this.ticket.assignee = null
          }
        }
      }
    },
    isLoaded: function (newVal) {
      if (this.listAllEmployees.length > 0 && this.ticket && this.ticket.assignee && this.ticket.assignee.name && newVal) {
        this.ticket.assignee = this.listAllEmployees.find(x => x.value.id === this.ticket.assignee.userId)
        this.$nextTick(function () {
          this.hasChanged = false
        })
      }
    },
    listEntities: function (newVal) {
      if (newVal.length > 0) {
        this.getListAllowedAccounts(newVal)
      } else if (this.requestsData === '') {
        this.getAllNetworkPartner()
      }
    },
    typeOfConnection: function (item) {
      // this.indexNameChanging = true
      this.resetPendingConnection()
      if (item) {
        this.query = ''
        if (item.key === 'condominium') {
          this.query = 'condominiumOwnership:true'
        }
        if (item.key === 'object') {
          this.query = 'condominiumOwnership:false'
        }
      }
      setTimeout(() => {
        this.refrecheAutosuggestKey = Math.floor(Math.random() * 100)
        // this.indexNameChanging = false
      }, 200)
    },
    'ticket.assignee': function (newVal) {
      if (newVal && newVal.value) {
        ContactApi.resolveByMail(this.accountId, [newVal.value.email]).then(response => {
          if (response) {
            this.assignee = ([...response.persons, ...response.companies] as Contact[])[0]
          }
        })
      } else {
        this.assignee = null
      }
    },
    onSiteContactPersons: function (newVal) {
      if (newVal.length > 0) {
        this.ticket.onSiteContacts = newVal
      } else {
        this.ticket.onSiteContacts = []
      }
    },
    listLinks: function () {
      if (this.linksInit) {
        this.linksUpdated = true
      }
    },
    mailboxEntries: function () {
      if (this.id && this.ticket && this.ticket.id && this.linkedEmails.length === 0) {
        this.loadLinkedEmailsByTicketId(this.ticket.id)
      }
    }
  },
  mounted() {
    this.loadInstantSearch()
    if (this.isJanitorEditor) {
      this.availableTypes = ['TICKET_TYPE_TASK']
      this.ticket.type = 'TICKET_TYPE_TASK'
    }
    if (this.isOwnerModule || this.isAssetModule) {
      this.availableTypes = ['TICKET_TYPE_TASK']
    }
    if (this.fromBulk) {
      this.ticket.type = this.ticketType
    }
    if (this.ticketPriority !== '') {
      this.ticket.priority = this.ticketPriority
    }
    if (this.id) {
      TicketApi.getTicketByKey(this.id)
        .then(response => {
          this.ticket = response.ticket
          this.loadLinkedEmailsByTicketId(this.ticket.id)
          if (this.ticket.type === 'TICKET_TYPE_ORDER' && !this.availableTypes.includes('TICKET_TYPE_ORDER')) {
            this.availableTypes.push('TICKET_TYPE_ORDER')
          }
          if (this.ticket.links.length > 0) {
            this.listLinks = JSON.parse(JSON.stringify(this.ticket.links))
          }
          this.$nextTick(() => {
            this.linksInit = true
          })
          const description = JSON.parse(JSON.stringify(this.ticket.description))
          this.ticket.description = description.replace(/<br>/g, '\n')
          this.mountedReference(this.ticket.references)
          this.dates.startDate = this.ticket.startDate ? this.$options.filters.objectToDateInput(this.ticket.startDate) : ''
          this.dates.dueDate = this.ticket.dueDate ? this.$options.filters.objectToDateInput(this.ticket.dueDate) : ''
          // Use converted input (not raw API object) so sentinel empty dates stay optional
          this.hadDueDateOnLoad = !!this.dates.dueDate
          this.hasChanged = false
          const currentTicket = JSON.parse(JSON.stringify(this.ticket))
          if ((currentTicket.correspondences?.length > 0 && currentTicket.correspondences[0].companyName !== '')) {
            let ids = []
            let hasCompanyCor = false
            if (currentTicket.correspondences?.length > 0 && currentTicket.correspondences[0].contactId) {
              ids.push(currentTicket.correspondences[0].contactId)
              hasCompanyCor = true
            }
            if (ids.length > 0) {
              ContactApi.contactResolve(ids)
                .then(response => {
                  const contacts = [...response.companies, ...response.persons] as Contact[]
                  if (hasCompanyCor) {
                    const company = contacts.find(x => x.id === currentTicket.correspondences[0].contactId || ('contactPersons' in x && x.contactPersons.map(y => y.id).includes(currentTicket.correspondences[0].contactId)))
                    if (company) {
                      this.listEmployeeCorrespondencesCP = company.contactPersons || []
                      this.correspondencesContactPerson = [company]
                      this.$nextTick(() => {
                        const item = this.listEmployeeCorrespondencesCP.find(x => x.id === currentTicket.correspondences[0].contactId)
                        if (item) {
                          item.companyName = company.name
                          this.correspondences = item
                        }
                      })
                    }
                  }
                })
                .catch(e => {
                  console.log(e)
                  Vue.toasted.show(this.$t('message.loadingErrors.contacts'), { type: 'error' })
                })
            }
          }
          if (currentTicket.onSiteContacts.length > 0) {
            this.onSiteContactPersons = currentTicket.onSiteContacts.map(x => {
              x.objectID = x.contactId
              return x
            })
          } else if (currentTicket.onSiteContact) {
            currentTicket.onSiteContact.objectID = currentTicket.onSiteContact.contactId
            this.onSiteContactPersons = [currentTicket.onSiteContact]
          }
          if (currentTicket.correspondences?.length > 0 && currentTicket.correspondences[0].companyName === '') {
            if (currentTicket.correspondences.length > 0) {
              this.correspondencesContactPerson = currentTicket.correspondences
            }
          }
          if (this.listLinks.length > 0) {
            TicketApi.getTicketByIds(this.listLinks.map(x => x.ticketId))
              .then(response => {
                this.listTickets = response.tickets
              })
          }
          if (this.ticket.mediaIds.length > 0) {
            MediaApi.getListMedia(this.ticket.mediaIds)
              .then(response => {
                for (const media of response.media) {
                  this.mediasList.push(media)
                  this.initCount()
                }
              })
              .catch(() => {
                Vue.toasted.show(this.$t('message.loadingErrors.media'), { type: 'error' })
              })
          }
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.loadingErrors.ticket'), { type: 'error' })
        }).finally(() => {
        this.hasChanged = false
      })
    } else {
      if (this.listAllEmployees.length > 0 && !this.ticket.assignee) {
        this.ticket.assignee = this.listAllEmployees.find(x => x.value.id === this.$store.getters['user/getUserId'])
      }
    }
    if (this.requestsData !== '') {
      this.loadRequestsData()
    } else {
      if (this.defaultAssignee !== '') {
        this.ticket.assignee = this.listAllEmployees.find(x => x.value.id === this.defaultAssignee)
      } else if (this.ids.length > 0) {
        this.ticket.assignee = null
      } else if (this.listAllEmployees && this.listAllEmployees.length > 0 && !this.ticket.assignee) {
        this.ticket.assignee = this.listAllEmployees.find(x => x.value.id === this.$store.getters['user/getUserId'])
      }
      if (this.ticketType !== '') {
        this.ticket.type = this.ticketType
      }
    }
    this.loadData()
    this.loadLinkedEmailsFromQuery()
    this.$nextTick(async function () {
      if (this.requestsData === '') {
        await this.loadEmployees()
        if (this.references.length === 0 && this.listEntities.length === 0) {
          this.getAllNetworkPartner()
        }
      }
      this.hasChanged = false
    })
    this.loadChecklistTemplate()
  },
  methods: {
    ...mapActions('employee', ['loadEmployees']),
    ...mapGetters('employee', ['getEmployees']),
    getSortedTemplateForView(): TicketTemplateOption[] {
      return this.sortedTemplate
    },
    getFilteredMediaListForView(): TicketMediaView[] {
      return this.filteredMediaList
    },
    getRequestMediaListForView() {
      return this.listRequestsMedia as RequestMediaItem[]
    },
    getDeviceTypeLabel(deviceType: string) {
      const label = this.$tc('message.onBoarding.devices.typeValues.' + deviceType)
      return label.length > 50 ? label.slice(0, 50) + '...' : label
    },
    getChecklistStatusClass(status: string) {
      return status !== '' ? status.split('CHECKLIST_ITEM_STATUS_')[1].toLowerCase() : ''
    },
    ...mapMutations('message', ['setMailDetailMailObject']),
    loadLinkedEmailsFromQuery() {
      if (this.emailsData === '') {
        return
      }
      let data = null
      try {
        data = JSON.parse(this.emailsData)
      } catch (e) {
        return
      }
      if (!data || !data.id) {
        return
      }
      this.emailsDataForLinking = data
      MessageApi.listMailsByIds([data.id])
        .then(response => {
          this.linkedEmails = response && response.mails ? response.mails : []
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.loadingErrors.message'), { type: 'error' })
        })
    },
    loadLinkedEmailsByTicketId(ticketId) {
      if (!ticketId) {
        return
      }
      const getMailboxesForUserId = this.$store.getters['message/getMailboxesForUserId']
      const mailboxIds = getMailboxesForUserId ? getMailboxesForUserId(this.userId, false).map(x => x.id) : []
      if (mailboxIds.length === 0) {
        this.linkedEmails = []
        return
      }
      MessageApi.listMailsByReferenceIds([ticketId], 'FILTER_FOLDER_ALL', mailboxIds, 1, null, '')
        .then(response => {
          this.linkedEmails = response && response.mails ? response.mails.filter(x => x.mailType !== 'MAIL_TYPE_TICKET_NOTIFICATION') : []
        })
        .catch(e => {
          console.log(e)
          if (e && e.response && e.response.status === 403) {
            this.linkedEmails = []
            return
          }
          Vue.toasted.show(this.$t('message.loadingErrors.message'), { type: 'error' })
        })
    },
    showLinkedEmailDetail(mail) {
      if (this.$store.getters['user/isSendMailModalOpen'] === true) {
        return
      }
      this.setMailDetailMailObject(mail)
      this.$bvModal.show('modal-detail-mail')
    },
    unlinkLinkedEmail(email) {
      const emailId = email?.id || email?.objectID
      if (!emailId) {
        return
      }
      if (!this.ticket?.id) {
        this.emailsDataForLinking = null
        this.linkedEmails = this.linkedEmails.filter(x => (x.id || x.objectID) !== emailId)
        this.hasChanged = true
        return
      }
      if (!this.pendingLinkedEmailUnlinks.some(x => x.emailId === emailId)) {
        this.pendingLinkedEmailUnlinks.push({
          emailId,
          referenceIds: email.referenceIds || []
        })
      }
      this.linkedEmails = this.linkedEmails.filter(x => (x.id || x.objectID) !== emailId)
      this.hasChanged = true
    },
    async applyPendingLinkedEmailUnlinks(ticketId) {
      if (!ticketId || this.pendingLinkedEmailUnlinks.length === 0) {
        return
      }
      const operations = this.pendingLinkedEmailUnlinks.map(item => {
        return MessageApi.setReferenceIds([item.emailId], item.referenceIds.filter(x => x !== ticketId))
      })
      await Promise.all(operations)
      this.pendingLinkedEmailUnlinks = []
    },
    formatCostAbsorption(costAbsorption) {
      if (!costAbsorption || costAbsorption === 'COST_ABSORPTION_UNDEFINED') {
        return ''
      }
      return this.$t('message.ticketOverview.ticket.costs.costAbsorptionValues.' + costAbsorption)
    },
    formatCostShareValue(entry) {
      if (!entry || entry.costShareUnit === 'COST_SHARE_UNIT_UNDEFINED' || entry.costShareValue == null) {
        return ''
      }
      if (entry.costShareUnit === 'COST_SHARE_UNIT_PERCENT') {
        return entry.costShareValue + '%'
      }
      return this.$options.filters.formatPrice(entry.costShareValue, 'CHF')
    },
    loadChecklistTemplate() {
      TicketApi.listChecklistTemplate(this.accountId).then(response => {
        this.listChecklistTemplates = response.templates
      }).catch(e => {
        console.log(e)
        Vue.toasted.show(this.$t('message.loadingErrors.template'), { type: 'error' })
      })
    },
    getListTasks() {
      if (this.ticket && this.ticket.checklist.length > 0) {
        this.listTasks = this.ticket.checklist.map((item) => {
          const assignee = this.listAllEmployees.find(x => x.value.id === item.assignee?.userId) || null
          return {
            ...item,
            assignee: assignee
          }
        })
      }
    },
    async applyCheckListTemplate(templateId) {
      if (templateId) {
        const template = this.listChecklistTemplates.find(x => x.id === templateId)
        if (!template) {
          this.initialTasks = []
          this.listTasks = []
          return
        }
        let assignee = null
        if (template.assigneeUserId === '' && template.usePropertyAssignee) {
          const splitIds = typeof this.ids === 'string' && this.ids !== '' ? this.ids.split(',') : Array.isArray(this.ids) && this.ids.length > 0 ? this.ids : []
          if (splitIds.length > 0 && splitIds[0].startsWith('contact')) {
            assignee = this.listAllEmployees.find(y => y.value.id === this.userId)
          } else {
            const propertyAssignees = this.checkPropertyAssignee()
            const uniqueIds = [...new Set(propertyAssignees)] as string[]
            if (uniqueIds.length === 1) {
              const rspContact = await ContactApi.contactResolve(uniqueIds)
              let contact = null
              let user = null
              if (rspContact && rspContact.persons && rspContact.persons.length > 0) {
                contact = rspContact.persons[0]
              } else if (rspContact && rspContact.companies && rspContact.companies.length > 0) {
                contact = rspContact.companies[0]
              }
              if (contact) {
                user = await UserApi.getUserByMail(contact.email)
              }
              if (user) {
                assignee = this.listAllEmployees.find(y => y.value.id === user.id)
              }
            } else {
              assignee = this.listAllEmployees.find(y => y.value.id === this.userId)
            }
          }
        } else {
          assignee = this.listAllEmployees.find(y => y.value.id === template.assigneeUserId)
        }
        if (assignee) {
          this.ticket.assignee = assignee
        }
        if (template.checklistItemTitles.length > 0 || template.checklistItems.length > 0) {
          if (template.checklistItems.length === 0) {
            this.listTasks = template.checklistItemTitles.map((item, index) => {
              const internalId = Date.now() + Math.floor(Math.random() * 1000)
              const id = JSON.parse(JSON.stringify('task_' + internalId))
              return {
                id: id,
                title: item,
                note: '',
                order: index + 1,
                status: 'CHECKLIST_ITEM_STATUS_OPEN',
                assignee: assignee
              }
            })
          } else {
            this.listTasks = template.checklistItems.map((item, index) => {
              const internalId = Date.now() + Math.floor(Math.random() * 1000)
              const id = JSON.parse(JSON.stringify('task_' + internalId))
              const assigneeTask = item.assigneeUserId !== '' && !item.useChecklistAssignee ? this.listAllEmployees.find(y => y.value.id === item.assigneeUserId) : item.useChecklistAssignee ? assignee : ''
              return {
                id: id,
                title: item.title,
                note: '',
                order: index + 1,
                status: 'CHECKLIST_ITEM_STATUS_OPEN',
                assignee: assigneeTask
              }
            })
          }
        } else {
          this.initialTasks = []
          this.listTasks = []
        }
        if (template.mediaIds.length > 0) {
          this.mediasList = this.mediasList.filter(x => !template.mediaIds.includes(x.id))
          MediaApi.getListMedia(template.mediaIds)
            .then(response => {
              for (const media of response.media) {
                this.mediasList.push(media)
                this.ticket.mediaIds.push(media.id)
                this.initCount()
              }
            })
            .catch(() => {
              Vue.toasted.show(this.$t('message.loadingErrors.media'), { type: 'error' })
            })
        }
      } else {
        this.initialTasks = []
        this.listTasks = []
      }
    },
    checkPropertyAssignee() {
      const contactIds = []
      this.references.forEach(ref => {
        if (ref.property) {
          if (this.isAdminModule) {
            if (ref.property.assignees.contactIdAssigneeAdmin !== '') {
              contactIds.push(ref.property.assignees.contactIdAssigneeAdmin)
            }
          }
          if (this.isAccountingModule) {
            if (ref.property.assignees.contactIdAssigneeAccounting !== '') {
              contactIds.push(ref.property.assignees.contactIdAssigneeAccounting)
            }
          }
          if (this.isOwnerModule) {
            if (ref.property.assignees.contactIdAssigneeOwner !== '') {
              contactIds.push(ref.property.assignees.contactIdAssigneeOwner)
            }
          }
          if (this.isAssetModule) {
            if (ref.property.assignees.contactIdAssigneeAsset !== '') {
              contactIds.push(ref.property.assignees.contactIdAssigneeAsset)
            }
          }
        }
      })
      return contactIds
    },
    deleteTicketClicked() {
      this.showDeleteTicketModal = true
      this.$nextTick(() => {
        this.$refs.deleteConfirmationModal.show()
      })
    },
    hideDeleteConfirmationModal() {
      this.$bvModal.hide('deleteConfirmationModal')
      this.showDeleteTicketModal = false
    },
    confirmDeletion() {
      TicketApi.deleteTicket(this.ticket.id)
        .then(() => {
          this.redirectToDraftOverview()
          Vue.toasted.show(this.$t('message.successMessages.deleteTicket'), { type: 'success' })
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.deleteErrors.ticket'), { type: 'error' })
        })
    },
    redirectToDraftOverview() {
      let query = null
      if (this.prevRoutePath.includes('/tickets/')) {
        query = this.prevRouteQuery
      }
      if (this.isOwnerModule) {
        this.$router.push({
          name: 'OwnerDraftTicketOverview',
          query: query
        }, () => {
        })
      } else if (this.isAdminModule) {
        this.$router.push({
          name: 'AdminDraftTicketOverview',
          query: query
        }, () => {
        })
      } else if (this.isAssetModule) {
        this.$router.push({
          name: 'AssetDraftTicketOverview',
          query: query
        }, () => {
        })
      } else if (this.isAccountingModule) {
        this.$router.push({
          name: 'AccountingDraftTicketOverview',
          query: query
        }, () => {
        })
      } else {
        this.$router.push({
          name: 'MarketingDraftTicketOverview',
          query: query
        }, () => {
        })
      }
    },
    normalizeIds(ids: string | string[] | undefined | null): string[] {
      if (!ids) {
        return []
      }
      const values = Array.isArray(ids) ? ids : ids.split(',')
      return [...new Set(values.map(id => id?.trim()).filter(Boolean))]
    },
    async loadData() {
      let arr = []
      if (((typeof this.ids === 'string' || this.ids instanceof String) && this.ids !== '') || this.ids.length > 0) {
        if (typeof this.ids === 'string' || this.ids instanceof String) {
          arr = [this.ids]
        } else {
          arr = this.ids
        }
      }
      const objectIds = this.normalizeIds(this.objectIds)
      const buildingIds = this.normalizeIds(this.buildingIds)
      const propertyIds = this.normalizeIds(this.propertyIds)
      const scopedDraftSource = this.source === 'draftOrder'
      const scopedReferenceType = scopedDraftSource
        ? (objectIds.length > 0 ? 'object' : buildingIds.length > 0 ? 'building' : propertyIds.length > 0 ? 'property' : '')
        : ''
      const allowArrReferenceFallback = !scopedDraftSource
      if ((objectIds.length > 0 && (scopedReferenceType === '' || scopedReferenceType === 'object')) || (allowArrReferenceFallback && arr.length > 0 && arr[0].startsWith('obj'))) {
        const scopedObjectIds = objectIds.length > 0 ? objectIds : arr
        const objectIdsToLoad = this.normalizeIds(scopedObjectIds)
        if (objectIdsToLoad.length > 0) {
          await ObjectApi.listObjectsByIds(objectIdsToLoad)
          .then(response => {
            response.objects.forEach(element => {
              this.addToReferences({ item: element })
            })
          })
        }
      }
      if ((buildingIds.length > 0 && (scopedReferenceType === '' || scopedReferenceType === 'building')) || (allowArrReferenceFallback && arr.length > 0 && arr[0].startsWith('building'))) {
        const scopedBuildingIds = buildingIds.length > 0 ? buildingIds : arr
        const buildingIdsToLoad = this.normalizeIds(scopedBuildingIds)
        if (buildingIdsToLoad.length > 0) {
          await ObjectApi.getBuildingsListByIds(buildingIdsToLoad)
          .then(response => {
            response.buildings.forEach(element => {
              this.addToReferences({ item: element })
            })
          })
        }
      }
      if ((propertyIds.length > 0 && (scopedReferenceType === '' || scopedReferenceType === 'property')) || (allowArrReferenceFallback && arr.length > 0 && arr[0].startsWith('prop'))) {
        const scopedPropertyIds = propertyIds.length > 0 ? propertyIds : arr
        const propertyIdsToLoad = this.normalizeIds(scopedPropertyIds)
        if (propertyIdsToLoad.length > 0) {
          await ObjectApi.getPropertyListByIds(propertyIdsToLoad)
          .then(response => {
            response.properties.forEach(element => {
              this.addToReferences({ item: element })
            })
          })
        }
      }
      if (this.tenancyIds || (arr.length > 0 && arr[0].startsWith('tenancy'))) {
        const tenancyIds = this.tenancyIds ? typeof this.tenancyIds === 'string' ? this.tenancyIds.split(',') : this.tenancyIds : arr
        await ObjectApi.getTenanciesByIds(tenancyIds)
          .then(response => {
            response.tenancies.forEach(element => {
              this.addToReferences({ item: element })
            })
          })
      }
      if (this.condoOwnerIds || (arr.length > 0 && arr[0].startsWith('condominium'))) {
        const condoOwnerIds = this.condoOwnerIds ? typeof this.condoOwnerIds === 'string' ? this.condoOwnerIds.split(',') : this.condoOwnerIds : arr
        await ObjectApi.getCondominiumByIds(condoOwnerIds)
          .then(response => {
            response.condominiums.forEach(element => {
              this.addToReferences({ item: element })
            })
          })
      }
      if (this.contactIds || (arr.length > 0 && arr[0].startsWith('contact'))) {
        const contactIds = this.contactIds ? typeof this.contactIds === 'string' ? this.contactIds.split(',') : this.contactIds : arr
        if (contactIds.length > 0) {
          await ContactApi.contactResolve(contactIds)
            .then(response => {
              ([...response.persons, ...response.companies] as Contact[]).filter(x => contactIds.includes(x.id)).forEach(element => {
                this.addToReferences({ item: element })
              })
            })
        }
      }
      if (arr.length > 0 && arr[0].startsWith('room')) {
        RoomApi.listById(arr)
          .then(response => {
            response.rooms.forEach(element => {
              this.addToReferences({ item: element })
            })
          })
      }
      if (arr.length > 0 && arr[0].startsWith('req')) {
        RequestApi.listByIds(arr)
          .then(response => {
            response.requests.forEach(element => {
              this.addToReferences({ item: element })
            })
          })
      }
      if (arr.length > 0 && arr[0].startsWith('device')) {
        ObjectApi.listDevicesByIds(arr)
          .then(response => {
            response.devices.forEach(element => {
              this.addToReferences({ item: element })
            })
          })
      }
      if (this.deviceIds) {
        const deviceIds = typeof this.deviceIds === 'string' ? this.deviceIds.split(',') : this.deviceIds
        if (deviceIds.length > 0) {
          ObjectApi.listDevicesByIds(deviceIds)
            .then(response => {
              response.devices.forEach(element => {
                this.addToReferences({ item: element })
              })
            })
        }
      }
      const listEntityIds = arr.filter(x => !x.startsWith('req'))
      const appendListEntityIds = (ids) => {
        this.normalizeIds(ids).forEach(id => {
          if (id && !id.startsWith('req') && !listEntityIds.includes(id)) {
            listEntityIds.push(id)
          }
        })
      }
      appendListEntityIds(this.objectIds)
      appendListEntityIds(this.buildingIds)
      appendListEntityIds(this.propertyIds)
      appendListEntityIds(this.tenancyIds)
      appendListEntityIds(this.condoOwnerIds)
      appendListEntityIds(this.contactIds)
      appendListEntityIds(this.deviceIds)
      this.listEntities = listEntityIds
    },
    filterHitsWithObjectId(hits) {
      return hits.filter(x => x && x.objectID)
    },
    getSuggestion(hits) {
      return hits.filter(x => x.objectID).concat(hits.flatMap(hit => {
        if ('contactPersons' in hit && hit.contactPersons.length > 0) {
          return hit.contactPersons.map(cp => {
            cp.objectID = cp.id
            return cp
          })
        } else {
          return []
        }
      }))
    },
    translatePlaceholders(message = '') {
      return new Promise((resolve) => {
        const list = []
        let contactId = ''
        let description = ''
        if (this.ticket.type === 'TICKET_TYPE_ORDER' && this.ticket.correspondences.length > 0) {
          contactId = this.correspondences ? this.correspondences.id : this.correspondencesContactPerson[0].objectID
        } else if (this.ticket.type === 'TICKET_TYPE_TASK' && this.assignee) {
          contactId = this.assignee.id
        }
        if (this.references.length > 0) {
          this.references.forEach(element => {
            if (element.object) {
              list.push({
                contactId: contactId,
                objectId: element.object.id || element.object.objectID
              })
            }
            if (element.building && !element.object) {
              list.push({
                contactId: contactId,
                buildingId: element.building.id || element.building.objectID
              })
            }
            if (element.property && !element.building) {
              list.push({
                contactId: contactId,
                propertyId: element.property.id || element.property.objectID
              })
            }
            if (element.contact) {
              list.push({
                contactId: element.contact.id || element.contact.objectID,
                objectId: ''
              })
            }
          })
        } else if (contactId !== '') {
          list.push({
            contactId: contactId,
            objectId: ''
          })
        }
        if (list.length > 0) {
          // Escape owner placeholders if no property/building/object is referenced,
          // so unresolved tags remain visible after API processing instead of being replaced with empty strings.
          const hasPropertyRef = list.some(item =>
            (item.objectId && item.objectId !== '') ||
            (item.buildingId && item.buildingId !== '') ||
            (item.propertyId && item.propertyId !== '')
          )
          const EIGENTUEMER_ADDR_TOKEN = '##EIGENTUEMER_ADRESSE_PH##'
          const EIGENTUEMER_TOKEN = '##EIGENTUEMER_PH##'
          let messageToSend = message
          if (!hasPropertyRef) {
            messageToSend = messageToSend
              .split('#eigentuemer.addresse#').join(EIGENTUEMER_ADDR_TOKEN)
              .split('#eigentuemer#').join(EIGENTUEMER_TOKEN)
          }
          MessageApi.bulkRenderText(list, messageToSend, '').then(response => {
            if (response?.entries.length > 0) {
              response?.entries.forEach(element => {
                if (element?.text) {
                  description += element.text + '\n\n'
                }
              })
              if (!hasPropertyRef) {
                description = description
                  .split(EIGENTUEMER_ADDR_TOKEN).join('#eigentuemer.addresse#')
                  .split(EIGENTUEMER_TOKEN).join('#eigentuemer#')
              }
              resolve(description)
            }
          }).catch(e => {
            console.log(e)
            resolve(message)
          })
        } else {
          resolve(message)
        }
      })
    },
    // Description function
    // in this function we get the data from the requests and try to find the assignee if there's one
    // then we get the documents from the requests and we add them to the list of documents
    async loadRequestsData() {
      const data = JSON.parse(this.requestsData)
      const promises = []
      this.listRequestsMedia = []
      this.ticket.title = data.title
      await this.getListEmployees([data.objectOwner])
      if (this.defaultAssignee !== '') {
        ContactApi.listEmployees(data.objectOwner)
          .then(response => {
            this.listEmployeeContact = response.persons
            this.assignee = response.persons.find(x => x.id === this.defaultAssignee)
          })
      }
      data.listDocsPerRequest.forEach(docs => {
        promises.push(MediaApi.listDocuments(docs.documents))
      })
      Promise.all(promises).then(response => {
        response.forEach((rsp, index) => {
          const idsAndNames = rsp.documents.map(x => ({
            id: x.versions[x.versions.length - 1].mediaId,
            mediumName: x.name
          }))
          const nameOfMergedFile = data.listDocsPerRequest[index].contacts.map(x => x.name).join(', ')
          MediaApi.mergeAllDocuments(idsAndNames, nameOfMergedFile).then(async response => {
            const file = response.media
            const binary = window.atob(file)
            const len = binary.length
            const buffer = new ArrayBuffer(len)
            const view = new Uint8Array(buffer)
            for (let i = 0; i < len; i++) {
              view[i] = binary.charCodeAt(i)
            }
            const blob = new Blob([view])
            // check if file type is pdf
            const pdfBytePattern = '25504446'
            const fileHeader = await this.getFileHeader(blob)
            const isPdf = fileHeader === pdfBytePattern

            const link = document.createElement('a')
            link.href = window.URL.createObjectURL(blob)
            this.listRequestsMedia.push({
              id: data.listDocsPerRequest[index].requestId,
              filename: nameOfMergedFile + (isPdf ? '.pdf' : '.zip'),
              blob: blob,
              requestId: data.listDocsPerRequest[index].requestId
            })
          }).catch(e => {
            console.log(e)
            Vue.toasted.show(this.$t('message.applicationForm.creditTrust.certificateOrderError'), { type: 'error' })
          })
        })
      })
    },
    // adaptEmailData() {
    //   const data = JSON.parse(this.emailsData)
    //   this.ticket.type = this.ticketType
    //   if (data.media?.length > 0) {
    //     for (const media of data.media) {
    //       this.mediasList.push(media)
    //       this.ticket.mediaIds.push(media.id)
    //     }
    //   }
    // },
    async downloadRequestsFile(blob, name) {
      const link = document.createElement('a')
      link.href = window.URL.createObjectURL(blob)
      link.download = name
      link.click()
    },
    deleteRequestMedia(_event, requestId) {
      this.listRequestsMedia = this.listRequestsMedia.filter(doc => doc.requestId !== requestId)
    },
    setRoomId(source, id) {
      if (source === 'building') {
        localStorage.setItem('expandBuildingRoomId', id)
      } else if (source === 'object') {
        localStorage.setItem('expandObjectRoomId', id)
      }
    },
    openRoomConnection(ref, newTab = false) {
      if (!ref?.room || !this.canOpenRoomConnection(ref)) {
        return
      }
      this.navigateToRoom(ref.room, ref.room.id, newTab, {
        buildingId: ref.object?.buildingId || ref.building?.id || ref.building?.objectID,
        objectId: ref.object?.objectID || ref.object?.id ||
          (ref.room.referenceId?.startsWith('obj') ? ref.room.referenceId : undefined)
      })
    },
    canOpenRoomConnection(ref) {
      if (!ref?.room?.referenceId) {
        return false
      }
      if (ref.room.referenceId.startsWith('building')) {
        return true
      }
      if (ref.room.referenceId.startsWith('obj')) {
        return !!(ref.object?.buildingId || ref.building?.id || ref.building?.objectID)
      }
      return false
    },
    setMortgageId(source, id) {
      if (source === 'property') {
        localStorage.setItem('expandPropertyMortgageId', id)
      }
    },
    setInsuranceId(source, id) {
      if (source === 'building') {
        localStorage.setItem('expandBuildingInsuranceId', id)
      } else if (source === 'property') {
        localStorage.setItem('expandPropertyInsuranceId', id)
      }
    },
    // generate description for 'property'
    getPropertyDescription(property) {
      let description = ''
      description = description + '\n' + this.$t('message.createOrder.property') + ' ' + property.name
      description = description + '\n' + this.$t('message.createOrder.city') + ' ' + property.address.zip + ' ' + property.address.city + '\n'
      return description
    },
    // generate description for 'building'
    getBuildingDescription(building) {
      let description = ''
      description = description + '\n' + this.$t('message.createOrder.address') + ' ' + this.convertAddress(building.address) + '\n'
      return description
    },
    // generate description for 'object', 'condominium', 'tenancy' and 'condoOwner'
    getObjectDescription(object) {
      if (!object) {
        return ''
      }
      let description = ''
      const categoryKey = (object.category || 'UNDEFINED').toLowerCase()
      const objectNumber = this.getObjectLocationNumber(object)
      const floor = object.floor && object.floor !== -10000
        ? object.floor
        : (object.characteristics?.floor !== -10000 ? object.characteristics?.floor : null)
      description = description + '\n' + this.$t('message.createOrder.address') + ' ' + this.convertAddress(object.address)
      description = description + '\n' + this.$t('message.createOrder.location') + ' ' + (object.name || '') + (objectNumber ? ', ' + objectNumber : '') + ', ' + this.$t('message.onBoarding.buildings.objects.category.' + categoryKey) + (floor != null ? (' - ' + this.formatFloor(floor)) : '') + '\n'
      return description
    },
    generateDescriptionFromReference(reference) {
      if (!reference) {
        return ''
      }
      let description = ''
      if (reference.object) {
        description += this.getObjectDescription(reference.object)
      } else if (reference.building) {
        description += this.getBuildingDescription(reference.building)
      } else if (reference.property) {
        description += this.getPropertyDescription(reference.property)
      }
      if (reference.device) {
        description += this.getDeviceDescription(reference.device)
      }
      return description
    },
    generateDescription(item, index = null) {
      const reference = typeof index === 'number' ? this.references[index] : null
      if (reference) {
        const description = this.generateDescriptionFromReference(reference)
        if (description !== '') {
          this.generatedDescription += description
        }
        return
      }
      let description = ''
      if ((item?.id?.startsWith('obj') || item?.objectID?.startsWith('obj')) || (item?.id?.startsWith('condominium') || item?.objectID?.startsWith('condominium')) || (item?.id?.startsWith('tenancy') || item?.objectID?.startsWith('tenancy'))) {
        description = this.getObjectDescription(item)
      } else if (item?.id?.startsWith('building') || item?.objectID?.startsWith('building')) {
        description = this.getBuildingDescription(item)
      } else if (item?.id?.startsWith('prop') || item?.objectID?.startsWith('prop')) {
        description = this.getPropertyDescription(item)
      }
      if (description !== '') {
        this.generatedDescription += description
      }
    },
    rebuildGeneratedDescription() {
      this.generatedDescription = ''
      this.references.forEach((reference, index) => {
        this.generateDescription(this.getReferenceById(this.listEntities[index], index), index)
      })
    },
    toggleListTickets() {
      if (!this.showTicketPreview) {
        this.$v.ticket.$touch()
        if (!this.$v.ticket.$invalid) {
          if (this.ticket.type === 'TICKET_TYPE_ORDER' &&
              this.ticket.correspondences.length > 0 &&
              !this.ticket.correspondences[0].email) {
            Vue.toasted.show(this.$t('message.savingErrors.operatorHasNoEmail'), { type: 'error' })
            return
          }
          this.createTickets()
        } else {
          Vue.toasted.show(this.$t('message.savingErrors.mandatoryMissing'), { type: 'error' })
        }
      } else {
        this.showTicketPreview = !this.showTicketPreview
      }
    },
    async createTickets() {
      this.disableBtns = true
      await this.ensureReferencesResolved()
      if (!this.id) {
        this.rebuildGeneratedDescription()
      }
      const promises = []
      const ticket = JSON.parse(JSON.stringify(this.ticket))
      this.ticketPreview = JSON.parse(JSON.stringify(this.ticket))
      this.ticketPreview.links = this.listLinks
      this.ticketPreview.status = 'TICKET_STATUS_OPEN'
      if (ticket.description.includes('#anrede#') || ticket.description.includes('#eigentuemer#') || ticket.description.includes('#eigentuemer.addresse#')) {
        this.ticketPreview.description = await this.translatePlaceholders(ticket.description)
      } else {
        this.ticketPreview.description = ticket.description
      }
      const tasks = JSON.parse(JSON.stringify(this.listTasks))
      this.ticketPreview.checklist = tasks.map(x => {
        const taskAssignee = x.assignee
        const assignee = taskAssignee ? { accountId: taskAssignee.value.accountId, userId: taskAssignee.value.id } : null
        delete x.assignee
        return {
          assignee: assignee,
          ...x
        }
      })
      const creator = this.listAllEmployees.find(x => x.value.id === this.userId) || null
      promises.push(ContactApi.resolveByMail(this.accountId, creator?.value?.email ? [creator.value.email] : []))
      Promise.all(promises).then(response => {
        let contacts = []
        response.forEach(element => {
          contacts = contacts.concat(element.companies).concat(element.persons)
        })
        let currentAssigne = null
        if (ticket.assignee && this.assignee) {
          const employee = ticket.assignee?.value
          currentAssigne = {
            accountId: employee.accountId,
            accountName: ticket.assignee.company,
            name: employee.profile?.firstName + ' ' + employee.profile?.lastName,
            profilePictureUrl: employee.profile?.pictureUrl,
            userId: employee.id,
            address: this.assignee.address,
            email: employee.email,
            phone: employee.profile?.phoneNumber
          }
        }
        const creatorContact = contacts.find(x => x.ownerId === creator?.value?.accountId && x.email === creator?.value?.email) || null
        const creatorCompany = this.ownerAccounts.find(x => parseInt(x.id) === this.accountId) || null
        const creatorDetail = creator?.value
        let currentCreator = null
        if (creatorDetail) {
          currentCreator = {
            accountId: this.accountId,
            accountName: creatorCompany?.company?.name || null,
            name: creatorDetail.profile?.firstName + ' ' + creatorDetail.profile?.lastName,
            profilePictureUrl: creatorDetail.profile?.pictureUrl,
            userId: creatorDetail.id,
            address: creatorCompany?.company?.address || creatorContact?.address || '',
            email: creatorDetail.email,
            phone: creatorDetail.profile?.phoneNumber
          }
        }
        const { ticketReferences } = this.buildTicketReferencesFromElements({ trackOwnerId: false })
        this.ticketPreview.references = ticketReferences
        this.ticketPreview.creator = currentCreator
        this.ticketPreview.assignee = currentAssigne
        this.ticketPreview.description += this.generatedDescription
        this.disableBtns = false
        this.showTicketPreview = !this.showTicketPreview
      })
    },
    getListEmployees(listIds: string[]) {
      if (listIds.length > 0) {
        const normalizedListIds = listIds.reduce((acc, id) => {
          const parsed = Number(id)
          if (!Number.isNaN(parsed)) {
            acc.push(parsed)
          }
          return acc
        }, [] as number[])
        TicketApi.listTicketingEnabled(normalizedListIds).then(rsp => {
          if (rsp.enabledAccountIds.length > 0) {
            UserApi.listAllEmployees(rsp.enabledAccountIds).then(response1 => {
              this.currentEmployeeList = response1.employees as TicketEmployee[]
              if (this.currentEmployeeList.length > 0) {
                const currentIds = this.currentEmployeeList.reduce((acc, x) => {
                  const parsed = Number(x.accountId)
                  if (!Number.isNaN(parsed)) {
                    acc.push(parsed)
                  }
                  return acc
                }, [] as number[])
                const employeeList = this.employeeList as TicketEmployee[]
                const employeeIds = employeeList.reduce((acc, x) => {
                  const parsed = Number(x.accountId)
                  if (!Number.isNaN(parsed)) {
                    acc.push(parsed)
                  }
                  return acc
                }, [] as number[])
                const ids: number[] = [...new Set<number>([...currentIds, ...employeeIds])]
                if (ids.length > 0) {
                  UserApi.listAccountsByIds(ids)
                    .then(accounts => {
                      this.ownerAccounts = accounts.accounts
                      this.addAvailableEmployees()
                    })
                }
              } else {
                this.addAvailableEmployees()
              }
            })
          } else {
            this.addAvailableEmployees()
          }
        })
      }
    },
    getCommonElements(array) {
      return array.reduce((a, b) => a.filter(c => b.includes(c)))
    },
    getListAllowedAccounts(listIds) {
      ShareApi.listAllowedAccountIds(listIds).then(response => {
        // after getting the allowed accounts we get the common elements between the allowed accounts
        // then get the employees of these accounts
        const listAllowedAccountIds = response.result.map(x => x.allowedAccountIds)
        const accountIds = listAllowedAccountIds?.length ? this.getCommonElements(listAllowedAccountIds) : []
        if (accountIds.length > 0) {
          this.getListEmployees(accountIds)
        } else {
          this.getListEmployees([this.accountId])
        }
      })
    },
    getAllNetworkPartner() {
      if (this.subType && this.subType === 'owner') {
        ShareApi.listPartners(this.$store.getters['user/getAccountId'])
          .then(response => {
            if (response.targetAccountIds.length > 0) {
              this.getListEmployees(response.targetAccountIds)
            } else {
              this.addAvailableEmployees()
            }
          })
          .catch(e => {
            console.log(e)
            Vue.toasted.show(this.$t('message.loadingErrors.contacts'), { type: 'error' })
          })
      } else if (this.subType && this.subType.toLowerCase().includes('asset')) {
        ShareApi.listPartnersOfAsset(this.$store.getters['user/getAccountId'])
          .then(response => {
            if (response.partnerAccountIds.length > 0) {
              this.getListEmployees(response.partnerAccountIds)
            } else {
              this.addAvailableEmployees()
            }
          })
          .catch(e => {
            console.log(e)
            Vue.toasted.show(this.$t('message.loadingErrors.contacts'), { type: 'error' })
          })
      } else {
        ShareApi.listOwners(this.$store.getters['user/getAccountId'])
          .then(response => {
            if (response.sourceAccountIds.length > 0) {
              this.getListEmployees(response.sourceAccountIds)
            } else {
              this.addAvailableEmployees()
            }
          })
          .catch(e => {
            console.log(e)
            Vue.toasted.show(this.$t('message.loadingErrors.contacts'), { type: 'error' })
          })
      }
    },
    redirectToDeviceDetail(referenceId, deviceId) {
      const refItem = this.references.find(x =>
        x.device && (x.device.id === deviceId || x.device.objectID === deviceId)
      )
      const query = buildDeviceDetailQuery(
        deviceId,
        this.buildDeviceFiltersFromTicketReference(refItem)
      )
      if (referenceId.startsWith('obj') || referenceId.startsWith('building')) {
        let id = null
        if (referenceId.startsWith('building')) {
          id = referenceId
        } else if (refItem) {
          id = refItem.building?.id
        }
        if (id && deviceId) {
          if (this.isOwnerModule) {
            return {
              name: 'OwnerBuildingDetailsView',
              params: { id: id },
              query
            }
          } else if (this.isAdminModule) {
            return {
              name: 'AdminBuildingDetailsView',
              params: { id: id },
              query
            }
          } else if (this.isAssetModule) {
            return {
              name: 'AssetBuildingDetailsView',
              params: { id: id },
              query
            }
          } else if (this.isAccountingModule) {
            return {
              name: 'AccountingBuildingDetailsView',
              params: { id: id },
              query
            }
          }
        }
      } else {
        const id = referenceId
        if (this.isOwnerModule) {
          return {
            name: 'OwnerPropertyDetailsView',
            params: { id: id },
            query
          }
        } else if (this.isAdminModule) {
          return {
            name: 'AdminPropertyDetailsView',
            params: { id: id },
            query
          }
        } else if (this.isAssetModule) {
          return {
            name: 'AssetPropertyDetailsView',
            params: { id: id },
            query
          }
        } else if (this.isAccountingModule) {
          return {
            name: 'AccountingPropertyDetailsView',
            params: { id: id },
            query
          }
        }
      }
    },
    openVideo(media) {
      this.selectedObjectVideo = media.url
      this.$nextTick(() => {
        this.$refs['show-video'].show()
      })
    },
    searchAssignee(options, search) {
      return search.length
        ? options.filter(item => item.label.toLowerCase().includes(search.toLowerCase()) || item.company.toLowerCase().includes(search.toLowerCase()))
        : options
    },
    itemChanged(selected) {
      if (selected && selected.item) {
        this.searchForTickets = selected.item.key + ' ' + selected.item.title
      }
    },
    getSuggestionValue(suggestion) {
      return suggestion.item.key + ' ' + suggestion.item.title
    },
    addAvailableEmployees() {
      const array = []
      if (this.employeeList.length !== 0 && this.requestsData === '') {
        this.employeeList.filter(x => (x.blocked === false && x.deleted === false)).forEach((employee) => {
          const accountName = this.ownerAccounts.find(x => x.id === employee.accountId)
          array.push({
            label: (employee.profile.firstName ? employee.profile.firstName + ' ' : '') + employee.profile.lastName,
            value: employee,
            company: (accountName ? ' ' + (accountName.company ? accountName.company?.name : accountName.individual.firstName + ' ' + accountName.individual.lastName) : '')
          })
        })
      }
      if (this.currentEmployeeList.length !== 0) {
        this.currentEmployeeList.filter(x => (x.blocked === false && x.deleted === false)).forEach((employee) => {
          const accountName = this.ownerAccounts.find(x => x.id === employee.accountId)
          array.push({
            label: ((employee.profile.firstName ? employee.profile.firstName + ' ' : '') + employee.profile.lastName),
            value: employee,
            company: (accountName ? ' ' + (accountName.company ? accountName.company?.name : accountName.individual.firstName + ' ' + accountName.individual.lastName) : '')
          })
        })
      }

      const seen = new Set()
      const filtered = array.filter(({ value }) => {
        const isSupportUser = value.email.startsWith('support.') && value.email.endsWith('@my-prop.ch')
        if (seen.has(value.id) || isSupportUser) return false
        seen.add(value.id)
        return true
      })

      const accountUsers = filtered.filter(x => parseInt(x.value.accountId) === this.accountId).sort((a, b) => (a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1))
      const otherUsers = filtered.filter(x => parseInt(x.value.accountId) !== this.accountId).sort((a, b) => (a.company > b.company ? 1 : -1))
      this.listAllEmployees = accountUsers.concat(otherUsers)

      // Interested party selection owner
      // in the case if we have only one employee in the list
      if (this.requestsData !== '' && this.listAllEmployees.length === 1) {
        this.ticket.assignee = this.listAllEmployees[0]
      }

      // Interested party selection owner
      // in the case if we have more than one employee in the list ans the object has 'object.contacts.contactIdAssigneeOwner'
      if (this.requestsData !== '' && this.listAllEmployees.length > 1 && this.defaultAssignee !== '') {
        const contactAssignee = this.listEmployeeContact.find(x => x.id === this.defaultAssignee)
        this.ticket.assignee = this.listAllEmployees.find(y => y.value.email === contactAssignee.email)
      }
      this.getListTasks()
      this.isLoaded = true
    },
    checkIsDisabled(item) {
      if (this.typeOfLink) {
        if (this.typeOfLink === 'TICKET_LINK_TYPE_IS_BLOCKED_BY' && this.filteredTicketLinked('TICKET_LINK_TYPE_IS_BLOCKED_BY').length > 0) {
          for (const ticket of this.filteredTicketLinked('TICKET_LINK_TYPE_IS_BLOCKED_BY')) {
            if (item.id === ticket.ticketId) {
              return true
            }
          }
        } else if (this.typeOfLink === 'TICKET_LINK_TYPE_BLOCKS' && this.filteredTicketLinked('TICKET_LINK_TYPE_BLOCKS').length > 0) {
          for (const ticket of this.filteredTicketLinked('TICKET_LINK_TYPE_BLOCKS')) {
            if (item.id === ticket.ticketId) {
              return true
            }
          }
        } else if (this.typeOfLink === 'TICKET_LINK_TYPE_RELATES' && this.filteredTicketLinked('TICKET_LINK_TYPE_RELATES').length > 0) {
          for (const ticket of this.filteredTicketLinked('TICKET_LINK_TYPE_RELATES')) {
            if (item.id === ticket.ticketId) {
              return true
            }
          }
        } else {
          return false
        }
      }
      return false
    },
    getMediaUrl(media) {
      if (media.thumbnails === undefined || media.thumbnails === null || typeof media.thumbnails === 'undefined') {
        return null
      }
      for (const thumbnail of media.thumbnails) {
        if (thumbnail.type === 1 || thumbnail.type === 'M') {
          return thumbnail.url
        }
      }
      return media.url
    },
    initCount() {
      this.countDocs = 0
      this.countImg = 0
      this.countVideo = 0
    },
    getTickets() {
      TicketApi.ListTicketByQuery(this.searchForTickets)
        .then(response => {
          this.ticketsForLink = response.tickets
        })
    },
    filteredTicketLinked(type) {
      return this.listLinks.filter(x => x.type === type)
    },
    getContactRouteId(contact: NonNullable<TicketReferenceView['contact']>): string {
      if ('objectID' in contact && contact.objectID) {
        return contact.objectID
      }
      return contact.id
    },
    redirectToContactDetail(contactId) {
      if (contactId !== null) {
        if (this.isOwnerModule) {
          return {
            name: 'OwnerContactDetailsView',
            params: { id: contactId }
          }
        } else if (this.isAssetModule) {
          return {
            name: 'AssetContactDetailsView',
            params: { id: contactId }
          }
        } else if (this.isAccountingModule) {
          return {
            name: 'AccountingContactDetailsView',
            params: { id: contactId }
          }
        } else if (this.isMarketingModule) {
          return {
            name: 'MarketingContactDetailsView',
            params: { id: contactId }
          }
        } else {
          return {
            name: 'AdminContactDetailsView',
            params: { id: contactId }
          }
        }
      }
    },
    redirectToBuildingDetail(buildingId) {
      if (this.isOwnerModule) {
        return {
          name: 'OwnerBuildingDetailsView',
          params: { id: buildingId },
          query: { view: 'building' }
        }
      } else if (this.isAssetModule) {
        return {
          name: 'AssetBuildingDetailsView',
          params: { id: buildingId },
          query: { view: 'building' }
        }
      } else if (this.isAccountingModule) {
        return {
          name: 'AccountingBuildingDetailsView',
          params: { id: buildingId },
          query: { view: 'building' }
        }
      } else if (this.isAdminModule) {
        return {
          name: 'AdminBuildingDetailsView',
          params: { id: buildingId },
          query: { view: 'building' }
        }
      }
    },
    redirectToPropertyDetail(propertyId) {
      if (this.isOwnerModule) {
        return {
          name: 'OwnerPropertyDetailsView',
          params: { id: propertyId }
        }
      } else if (this.isAdminModule) {
        return {
          name: 'AdminPropertyDetailsView',
          params: { id: propertyId }
        }
      } else if (this.isAssetModule) {
        return {
          name: 'AssetPropertyDetailsView',
          params: { id: propertyId }
        }
      } else if (this.isAccountingModule) {
        return {
          name: 'AccountingPropertyDetailsView',
          params: { id: propertyId }
        }
      }
    },
    redirectToRequestDetail(objectId, requestId) {
      return {
        name: 'MarketingObjectDetailsView',
        params: { id: objectId },
        query: {
          view: 0,
          elementSelected: requestId
        }
      }
    },
    pushToRequestDetail(objectId, requestId) {
      this.$router.push({
        name: 'MarketingObjectDetailsView',
        params: { id: objectId },
        query: {
          view: 0,
          elementSelected: requestId
        }
      })
    },
    redirectToDetail(buildingId, objectId, view) {
      if (this.isOwnerModule) {
        return {
          name: 'OwnerBuildingDetailsView',
          params: { id: buildingId },
          query: {
            view: view,
            elementSelected: objectId
          }
        }
      } else if (this.isAdminModule) {
        return {
          name: 'AdminBuildingDetailsView',
          params: { id: buildingId },
          query: {
            view: view,
            elementSelected: objectId
          }
        }
      } else if (this.isAssetModule) {
        return {
          name: 'AssetBuildingDetailsView',
          params: { id: buildingId },
          query: {
            view: view,
            elementSelected: objectId
          }
        }
      } else if (this.isAccountingModule) {
        return {
          name: 'AccountingBuildingDetailsView',
          params: { id: buildingId },
          query: {
            view: view,
            elementSelected: objectId
          }
        }
      }
    },
    checkSelectedElement(id) {
      const pendingId = this.getConnectionId(this.pendingConnection)
      if (pendingId && pendingId === id) {
        return 'selected'
      }
      if (id.startsWith('condominium')) {
        if (this.references.find(x => x.condominium && (x.condominium.id === id || x.condominium.objectID === id))) {
          return 'selected'
        }
      }
      if (id.startsWith('tenancy')) {
        if (this.references.find(x => x.tenancy && (x.tenancy.id === id || x.tenancy.objectID === id))) {
          return 'selected'
        }
      }
      if (id.startsWith('contact')) {
        if (this.references.find(x => x.contact && (x.contact.id === id || x.contact.objectID === id))) {
          return 'selected'
        }
      }
      return ''
    },
    requiresDeviceStep(item) {
      if (!item) {
        return false
      }
      const itemId = item.objectID || item.id || ''
      return itemId.startsWith('prop') ||
        itemId.startsWith('building') ||
        itemId.startsWith('obj')
    },
    isRepeatableConnectionId(id) {
      return id.startsWith('prop') || id.startsWith('building') || id.startsWith('obj')
    },
    isSelectionBlocked(id) {
      if (!id || this.isRepeatableConnectionId(id)) {
        return false
      }
      return this.checkSelectedElement(id) === 'selected'
    },
    isPendingRepeatableConnectionAlreadyAdded() {
      const pendingId = this.getConnectionId(this.pendingConnection)
      if (!pendingId || !this.isRepeatableConnectionId(pendingId)) {
        return false
      }
      if (pendingId.startsWith('obj')) {
        return this.references.some(x => x.object && (x.object.id === pendingId || x.object.objectID === pendingId) && (!x.tenancy && !x.condominium && !x.device))
      }
      if (pendingId.startsWith('building')) {
        return this.references.some(x => x.building && (x.building.id === pendingId || x.building.objectID === pendingId) && !x.object && !x.device)
      }
      if (pendingId.startsWith('prop')) {
        return this.references.some(x => x.property && (x.property.id === pendingId || x.property.objectID === pendingId) && !x.building && !x.device)
      }
      return false
    },
    getConnectionId(item) {
      if (!item) {
        return null
      }
      return item.objectID || item.id || null
    },
    getDeviceConnectionId(device) {
      if (!device) {
        return null
      }
      return device.objectID || device.id || null
    },
    hasDeviceReference(device) {
      const deviceId = this.getDeviceConnectionId(device)
      if (!deviceId) {
        return false
      }
      return this.references.some(reference => {
        const existingDeviceId = this.getDeviceConnectionId(reference.device)
        return existingDeviceId === deviceId
      })
    },
    resetPendingConnection() {
      this.pendingConnection = null
      this.pendingConnectionDevices = []
      this.searchDeviceQuery = ''
      this.refrecheDeviceAutosuggestKey = Math.floor(Math.random() * 100)
    },
    async loadPendingConnectionDevices() {
      if (!this.pendingConnection) {
        return
      }
      const connectionId = this.getConnectionId(this.pendingConnection)
      if (!connectionId) {
        this.pendingConnectionDevices = []
        return
      }
      try {
        const response = await ObjectApi.getDevicesByReferenceIds([connectionId])
        this.pendingConnectionDevices = response?.devices || []
        this.refrecheDeviceAutosuggestKey = Math.floor(Math.random() * 100)
      } catch (e) {
        console.log(e)
        this.pendingConnectionDevices = []
        Vue.toasted.show(this.$t('message.loadingErrors.building'), { type: 'error' })
      }
    },
    deviceIndicesToSuggestions() {
      const query = (this.searchDeviceQuery || '').toLowerCase().trim()
      const data = this.pendingConnectionDevices.filter((item) => {
        if (!query) {
          return true
        }
        const searchable = [
          item.system,
          item.type ? this.$t('message.onBoarding.devices.typeValues.' + item.type) : '',
          item.brand,
          item.model
        ].filter(Boolean).join(' ').toLowerCase()
        return searchable.includes(query)
      })
      return [{ data: data.slice(0, 20) }]
    },
    onSelectDevice(selected) {
      if (!selected || !selected.item || !this.pendingConnection) {
        return
      }
      if (this.hasDeviceReference(selected.item)) {
        this.$nextTick(() => {
          this.searchDeviceQuery = ''
          this.refrecheDeviceAutosuggestKey = Math.floor(Math.random() * 100)
        })
        return
      }
      this.addPendingConnection(selected.item)
    },
    addPendingConnectionWithoutDevice() {
      if (!this.pendingConnection) {
        return
      }
      if (this.isPendingRepeatableConnectionAlreadyAdded()) {
        return
      }
      this.addPendingConnection(null)
    },
    addPendingConnection(device = null) {
      if (!this.pendingConnection) {
        return
      }
      this.listEntities.push(this.getConnectionId(this.pendingConnection))
      this.addToReferences({ item: this.pendingConnection }, device)
      this.$nextTick(() => {
        this.emailSuggestion = ''
        this.resetPendingConnection()
        this.refrecheAutosuggestKey = Math.floor(Math.random() * 100)
      })
    },
    formatDeviceMainLabel(device) {
      const typeLabel = device?.type && device.type !== 'DEVICE_TYPE_UNDEFINED'
        ? this.$t('message.onBoarding.devices.typeValues.' + device.type)
        : '-'
      const brand = device?.brand || ''
      const model = device?.model || ''
      if (!brand && !model) {
        return typeLabel
      }
      if (brand && model) {
        return `${typeLabel} (${brand} / ${model})`
      }
      return `${typeLabel} (${brand || model})`
    },
    formatDeviceDateLabel(device) {
      const installation = device?.installation ? this.$options.filters.objectToDate(device.installation) : '-'
      if (device?.uninstallation) {
        return `${installation} - ${this.$options.filters.objectToDate(device.uninstallation)}`
      }
      return installation
    },
    onSelect(selected) {
      if (selected && !this.isSelectionBlocked(selected.item.objectID)) {
        if (this.requiresDeviceStep(selected.item)) {
          this.pendingConnection = selected.item
          this.emailSuggestion = selected.item.name || ''
          this.refrecheAutosuggestKey = Math.floor(Math.random() * 100)
          this.loadPendingConnectionDevices()
          return
        }

        let alreadyExist = false
        const id = selected.item.objectID
        if (selected.item.objectID.startsWith('condominium')) {
          if (this.references.find(x => x.condominium && (x.condominium.id === id || x.condominium.objectID === id))) {
            this.references = this.references.filter(x => !x.condominium || (x.condominium && ((x.condominium.objectID && x.condominium.objectID !== id) || (x.condominium.id && x.condominium.id !== id))))
            alreadyExist = true
          }
        }
        if (selected.item.objectID.startsWith('tenancy')) {
          if (this.references.find(x => x.tenancy && (x.tenancy.id === id || x.tenancy.objectID === id))) {
            this.references = this.references.filter(x => !x.tenancy || (x.tenancy && ((x.tenancy.objectID && x.tenancy.objectID !== id) || (x.tenancy.id && x.tenancy.id !== id))))
            alreadyExist = true
          }
        }
        if (selected.item.objectID.startsWith('obj')) {
          if (this.references.find(x => x.object && (x.object.id === id || x.object.objectID === id) && (!x.tenancy && !x.condominium))) {
            this.references = this.references.filter(x => !x.object || (x.object && ((x.object.objectID && x.object.objectID !== id) || (x.object.id && x.object.id !== id) || (x.tenancy || x.condominium))))
            alreadyExist = true
          }
        }
        if (selected.item.objectID.startsWith('building')) {
          if (this.references.find(x => x.building && (x.building.id === id || x.building.objectID === id) && !x.object)) {
            this.references = this.references.filter(x => !x.building || (x.building && ((x.building.objectID && x.building.objectID !== id) || (x.building.id && x.building.id !== id) || x.object)))
            alreadyExist = true
          }
        }
        if (selected.item.objectID.startsWith('prop')) {
          if (this.references.find(x => x.property && (x.property.id === id || x.property.objectID === id) && !x.building)) {
            this.references = this.references.filter(x => !x.property || (x.property && ((x.property.objectID && x.property.objectID !== id) || (x.property.id && x.property.id !== id) || x.building)))
            alreadyExist = true
          }
        }
        if (selected.item.objectID.startsWith('contact')) {
          if (this.references.find(x => x.contact && (x.contact.id === id || x.contact.objectID === id))) {
            this.references = this.references.filter(x => !x.contact || (x.contact && ((x.contact.objectID && x.contact.objectID !== id) || (x.contact.id && x.contact.id !== id))))
            alreadyExist = true
          }
        }
        if (alreadyExist === false) {
          this.listEntities.push(id)
          this.addToReferences(selected)
        } else {
          this.listEntities = this.listEntities.filter(entityId => entityId !== id)
        }
        this.$nextTick(function () {
          this.emailSuggestion = ''
          this.refrecheAutosuggestKey = Math.floor(Math.random() * 100)
        })
      } else {
        this.$refs.autoSuggest_connections.internalValue = ''
      }
    },
    imageClicked(item) {
      if (item.url && item.url !== '') {
        this.galleryImages = [
          item.url
        ]
        this.$nextTick(function () {
          this.$viewerApi({
            images: this.galleryImages,
            options: this.viewerOptions
          })
        })
      }
    },
    addToReferences(selected, selectedDevice = null) {
      this.emailSuggestion = null
      const lengthOfConnections = this.references.length
      this.references.push({
        request: null,
        object: null,
        building: null,
        property: null,
        condominium: null,
        tenancy: null,
        contact: null,
        device: null,
        room: null,
        mortgage: null,
        insurance: null,
        listContact: []
      })
      if (selected.item.objectID?.startsWith('condominium') || selected.item.id?.startsWith('condominium') || selected.item.objectID?.startsWith('tenancy') || selected.item.id?.startsWith('tenancy')) {
        if (selected.item.objectID?.startsWith('condominium') || selected.item.id?.startsWith('condominium')) {
          this.references[lengthOfConnections].condominium = selected.item
          const ownerContactIds = selected.item.owner?.contactIds || []
          if ((selected.item.id || selected.item.objectID) && ownerContactIds.length > 0) {
            ContactApi.contactResolve(ownerContactIds)
              .then(responseCon => {
                this.references[lengthOfConnections].listContact = ([...responseCon.persons, ...responseCon.companies] as Contact[]).filter(c => ownerContactIds.includes(c.id))
              })
          }
        } else if (selected.item.objectID?.startsWith('tenancy') || selected.item.id?.startsWith('tenancy')) {
          this.references[lengthOfConnections].tenancy = selected.item
          if (selected.item.id || selected.item.objectID) {
            const tenantIds = selected.item.tenants?.[0]?.contactIds || selected.item.tenant?.contactIds || []
            if (tenantIds.length > 0) {
              ContactApi.contactResolve(tenantIds)
                .then(responseCon => {
                  this.references[lengthOfConnections].listContact = ([...responseCon.persons, ...responseCon.companies] as Contact[]).filter(c => tenantIds.includes(c.id))
                })
            }
          }
        }
        ObjectApi.getObject(this.getLinkedObjectId(selected.item))
          .then(response => {
            this.references[lengthOfConnections].object = response.object
            ObjectApi.getBuildingByObjectId(this.references[lengthOfConnections].object.id).then(response => {
              this.references[lengthOfConnections].building = response.building
              ObjectApi.getPropertiesByBuildingIds([this.references[lengthOfConnections].building.id])
                .then(response => {
                  this.references[lengthOfConnections].property = response.properties.length > 0 ? response.properties[0] : null
                })
            }).catch(e => {
              console.log(e)
              Vue.toasted.show(this.$t('message.loadingErrors.advertisement'), { type: 'error' })
            })
          })
      }
      if (selected.item.objectID?.startsWith('obj') || selected.item.id?.startsWith('obj')) {
        this.references[lengthOfConnections].object = selected.item
        ObjectApi.getBuildingByObjectId(selected.item.objectID || selected.item.id).then(response => {
          this.references[lengthOfConnections].building = response.building
          ObjectApi.getPropertiesByBuildingIds([this.references[lengthOfConnections].building.id])
            .then(response => {
              this.references[lengthOfConnections].property = response.properties[0]
            })
        }).catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.loadingErrors.advertisement'), { type: 'error' })
        })
      }
      if (selected.item.objectID?.startsWith('building') || selected.item.id?.startsWith('building')) {
        this.references[lengthOfConnections].building = selected.item
        ObjectApi.getPropertiesByBuildingIds([selected.item.objectID || selected.item.id])
          .then(response => {
            this.references[lengthOfConnections].property = response.properties[0]
          })
      }
      if (selected.item.objectID?.startsWith('prop') || selected.item.id?.startsWith('prop')) {
        this.references[lengthOfConnections].property = selected.item
      }
      if (selected.item.objectID?.startsWith('contact') || selected.item.id?.startsWith('contact')) {
        this.references[lengthOfConnections].contact = selected.item
      }
      if (selected.item.objectID?.startsWith('device') || selected.item.id?.startsWith('device')) {
        this.references[lengthOfConnections].device = selected.item

        if (selected.item.referenceId?.startsWith('obj')) {
          ObjectApi.getObject(selected.item.referenceId)
            .then(response => {
              this.references[lengthOfConnections].object = response.object
              ObjectApi.getBuildingByObjectId(this.references[lengthOfConnections].object.id).then(response => {
                this.references[lengthOfConnections].building = response.building
                ObjectApi.getPropertiesByBuildingIds([this.references[lengthOfConnections].building.id])
                  .then(response => {
                    this.references[lengthOfConnections].property = response.properties[0]
                  })
              }).catch(e => {
                console.log(e)
                Vue.toasted.show(this.$t('message.loadingErrors.advertisement'), { type: 'error' })
              })
            })
        }
        if (selected.item.referenceId?.startsWith('building')) {
          ObjectApi.getBuilding(selected.item.referenceId)
            .then(response => {
              this.references[lengthOfConnections].building = response.building
              ObjectApi.getPropertiesByBuildingIds([response.building.id])
                .then(response => {
                  this.references[lengthOfConnections].property = response.properties[0]
                })
            })
        }
        if (selected.item.referenceId?.startsWith('prop')) {
          ObjectApi.getPropertyListByIds([selected.item.referenceId])
            .then(response => {
              this.references[lengthOfConnections].property = response.properties[0]
            })
        }
      }
      if (selectedDevice) {
        this.references[lengthOfConnections].device = selectedDevice
        if (selectedDevice.referenceId?.startsWith('obj')) {
          ObjectApi.getObject(selectedDevice.referenceId)
            .then(response => {
              this.references[lengthOfConnections].object = response.object
              ObjectApi.getBuildingByObjectId(this.references[lengthOfConnections].object.id).then(response => {
                this.references[lengthOfConnections].building = response.building
                ObjectApi.getPropertiesByBuildingIds([this.references[lengthOfConnections].building.id])
                  .then(response => {
                    this.references[lengthOfConnections].property = response.properties[0]
                  })
              }).catch(e => {
                console.log(e)
                Vue.toasted.show(this.$t('message.loadingErrors.advertisement'), { type: 'error' })
              })
            })
        }
        if (selectedDevice.referenceId?.startsWith('building')) {
          ObjectApi.getBuilding(selectedDevice.referenceId)
            .then(response => {
              this.references[lengthOfConnections].building = response.building
              ObjectApi.getPropertiesByBuildingIds([response.building.id])
                .then(response => {
                  this.references[lengthOfConnections].property = response.properties[0]
                })
            })
        }
        if (selectedDevice.referenceId?.startsWith('prop')) {
          ObjectApi.getPropertyListByIds([selectedDevice.referenceId])
            .then(response => {
              this.references[lengthOfConnections].property = response.properties[0]
            })
        }
      }
      if (selected.item.objectID?.startsWith('req') || selected.item.id?.startsWith('req')) {
        this.references[lengthOfConnections].request = selected.item
        if (selected.item.id || selected.item.objectID) {
          const cont = selected.item.requesters[0].contactId
          if (cont.length > 0) {
            ContactApi.contactResolve([cont])
              .then(response => {
                this.references[lengthOfConnections].listContact = ([...response.persons, ...response.companies] as Contact[]).filter(c => cont.includes(c.id))
              })
          }
          ObjectApi.getObject(selected.item.objectId)
            .then(response => {
              this.references[lengthOfConnections].object = response.object
              ObjectApi.getBuildingByObjectId(this.references[lengthOfConnections].object.id).then(response => {
                this.references[lengthOfConnections].building = response.building
                ObjectApi.getPropertiesByBuildingIds([this.references[lengthOfConnections].building.id])
                  .then(response => {
                    this.references[lengthOfConnections].property = response.properties[0]
                  })
              }).catch(e => {
                console.log(e)
                Vue.toasted.show(this.$t('message.loadingErrors.advertisement'), { type: 'error' })
              })
            })
        }
      }
      if (selected.item.id?.startsWith('room')) {
        this.references[lengthOfConnections].room = selected.item
        if (selected.item && selected.item.referenceId.startsWith('obj')) {
          ObjectApi.getObject(selected.item.referenceId)
            .then(response => {
              this.references[lengthOfConnections].object = response.object
              ObjectApi.getBuildingByObjectId(this.references[lengthOfConnections].object.id).then(response => {
                this.references[lengthOfConnections].building = response.building
                ObjectApi.getPropertiesByBuildingIds([this.references[lengthOfConnections].building.id])
                  .then(response => {
                    this.references[lengthOfConnections].property = response.properties[0]
                  })
              }).catch(e => {
                console.log(e)
                Vue.toasted.show(this.$t('message.loadingErrors.advertisement'), { type: 'error' })
              })
            })
        }
        if (selected.item && selected.item.referenceId.startsWith('building')) {
          ObjectApi.getBuilding(selected.item.referenceId).then(response => {
            this.references[lengthOfConnections].building = response.building
            ObjectApi.getPropertiesByBuildingIds([this.references[lengthOfConnections].building.id])
              .then(response => {
                this.references[lengthOfConnections].property = response.properties[0]
              })
          }).catch(e => {
            console.log(e)
            Vue.toasted.show(this.$t('message.loadingErrors.advertisement'), { type: 'error' })
          })
        }
      }
    },
    convertAddress(address) {
      if (!address) {
        return '-'
      }

      const addr = (address.careOf ? address.careOf : '') +
        (address.streetAddition ? address.streetAddition : '') +
        (address.street ? address.street + (address.streetNumber ? ' ' + address.streetNumber : '') : '') +
        (address.zip ? ' ' + address.zip : '') +
        (address.city ? ' ' + address.city : '')

      if (addr) {
        return addr
      } else {
        return ''
      }
    },
    getDeviceDescription(device = null) {
      if (device) {
        let description = '\n'
        if (device.referenceId.startsWith('prop')) {
          this.references.forEach(item => {
            const property = item.property
            if (item.device?.id === device.id && property && (device.referenceId === property?.id || device.referenceId === property?.objectID)) {
              description = description + '\n' + this.$t('message.createOrder.address') + ' ' + this.convertAddress(property.address)
              description = description + '\n' + this.$t('message.createOrder.location') + ' ' + property.name
            }
          })
        }
        if (device.referenceId.startsWith('building')) {
          this.references.forEach(item => {
            const building = item.building
            if (item.device?.id === device.id && building && (device.referenceId === building?.id || device.referenceId === building?.objectID)) {
              description = description + '\n' + this.$t('message.createOrder.address') + ' ' + this.convertAddress(building.address)
              description = description + '\n' + this.$t('message.createOrder.location') + ' ' + building.name
            }
          })
        }
        if (device.referenceId.startsWith('obj')) {
          this.references.forEach(item => {
            const object = item.object
            if (object && (device.referenceId === object.id || device.referenceId === object.objectID) &&
              (!item.device || item.device.id === device.id || item.device.objectID === device.objectID)) {
              const categoryKey = (object.category || 'UNDEFINED').toLowerCase()
              const objectNumber = this.getObjectLocationNumber(object)
              description = description + '\n' + this.$t('message.createOrder.address') + ' ' + this.convertAddress(object.address)
              description = description + '\n' + this.$t('message.createOrder.location') + ' ' + (object.name || '') + (objectNumber ? ', ' + objectNumber : '') + ', ' + this.$t('message.onBoarding.buildings.objects.category.' + categoryKey) + (object.characteristics?.floor !== -10000 ? ' - ' + this.formatFloor(object.characteristics?.floor) : '')
            }
          })
        }
        if (device.type !== '' && device.type !== 'DEVICE_TYPE_UNDEFINED') {
          description = description + '\n' + this.$t('message.createOrder.componentDevice') + ' ' + this.$t('message.onBoarding.devices.typeValues.' + device.type)
        }
        if (device.manufacturer !== '') {
          description = description + '\n' + this.$t('message.createOrder.manufacturer') + ' ' + device.manufacturer
        }
        if (device.brand !== '') {
          description = description + '\n' + this.$t('message.createOrder.brand') + ' ' + device.brand
        }
        if (device.model !== '') {
          description = description + '\n' + this.$t('message.createOrder.model') + ' ' + device.model
        }
        if (device.serialNumber !== '') {
          description = description + '\n' + this.$t('message.createOrder.serialNumber') + ' ' + device.serialNumber
        }
        if (device.assetNumberFm !== '') {
          description = description + '\n' + this.$t('message.createOrder.assetNumberFm') + ' ' + device.assetNumberFm
        }
        return description
      }
      return ''
    },
    mountedReference(references) {
      for (let index = 0; index < references.length; index++) {
        this.references.push({
          object: null,
          building: null,
          property: null,
          condominium: null,
          tenancy: null,
          contact: null,
          device: null,
          request: null,
          room: null,
          mortgage: null,
          insurance: null,
          listContact: []
        })
        const element = references[index].id
        this.listEntities.push(element)
        if (element.startsWith('condominium') || element.startsWith('tenancy')) {
          if (element.startsWith('condominium')) {
            ObjectApi.getCondominiumByIds([element])
              .then(response => {
                this.references[index].condominium = response.condominiums[0]
                if (this.references[index].condominium.owner.contactIds.length > 0) {
                  ContactApi.contactResolve(this.references[index].condominium.owner.contactIds)
                    .then(responseCon => {
                      this.references[index].listContact = [...responseCon.persons, ...responseCon.companies] as Contact[]
                    })
                }
                ObjectApi.getObject(this.references[index].condominium.objectId)
                  .then(responseObj => {
                    this.references[index].object = responseObj.object
                    ObjectApi.getBuildingByObjectId(this.references[index].object.id).then(responseBuild => {
                      this.references[index].building = responseBuild.building
                      ObjectApi.getPropertiesByBuildingIds([this.references[index].building.id])
                        .then(responseProp => {
                          this.references[index].property = responseProp.properties[0]
                        })
                    }).catch(e => {
                      console.log(e)
                      Vue.toasted.show(this.$t('message.loadingErrors.advertisement'), { type: 'error' })
                    })
                  })
              })
          } else if (element.startsWith('tenancy')) {
            ObjectApi.getTenanciesByIds([element])
              .then(response => {
                const tenancy = response.tenancies?.[0]
                if (!tenancy) {
                  return
                }
                this.references[index].tenancy = tenancy
                if (tenancy.tenant?.contactIds?.length > 0) {
                  ContactApi.contactResolve(tenancy.tenant.contactIds)
                    .then(responseCon => {
                      this.references[index].listContact = ([...responseCon.persons, ...responseCon.companies] as Contact[]).filter(c => tenancy.tenant.contactIds.includes(c.id))
                    })
                }
                ObjectApi.getObject(tenancy.objectId)
                  .then(response => {
                    this.references[index].object = response.object
                    ObjectApi.getBuildingByObjectId(this.references[index].object.id).then(response => {
                      this.references[index].building = response.building
                      ObjectApi.getPropertiesByBuildingIds([this.references[index].building.id])
                        .then(response => {
                          this.references[index].property = response.properties[0]
                        })
                    }).catch(e => {
                      console.log(e)
                      Vue.toasted.show(this.$t('message.loadingErrors.advertisement'), { type: 'error' })
                    })
                  })
              })
          }
        }
        if (element.startsWith('device') || element.startsWith('device')) {
          ObjectApi.listDevicesByIds([element])
            .then(response => {
              if (response.devices.length > 0) {
                const device = response.devices[0]
                this.references[index].device = device
                if (device.roomId) {
                  RoomApi.listById([device.roomId]).then(roomResponse => {
                    if (roomResponse.rooms.length > 0) {
                      this.references[index].room = roomResponse.rooms[0]
                    }
                  })
                }
                if (device.referenceId?.startsWith('obj')) {
                  ObjectApi.getObject(device.referenceId)
                    .then(response => {
                      this.references[index].object = response.object
                      ObjectApi.getBuildingByObjectId(this.references[index].object.id).then(response => {
                        this.references[index].building = response.building
                        ObjectApi.getPropertiesByBuildingIds([this.references[index].building.id])
                          .then(response => {
                            this.references[index].property = response.properties[0]
                          })
                      }).catch(e => {
                        console.log(e)
                        Vue.toasted.show(this.$t('message.loadingErrors.advertisement'), { type: 'error' })
                      })
                    })
                }
                if (device.referenceId?.startsWith('building')) {
                  ObjectApi.getBuilding(device.referenceId)
                    .then(response => {
                      this.references[index].building = response.building
                      ObjectApi.getPropertiesByBuildingIds([response.building.id])
                        .then(response => {
                          this.references[index].property = response.properties[0]
                        })
                    })
                }
                if (device.referenceId?.startsWith('prop')) {
                  ObjectApi.getPropertyListByIds([device.referenceId])
                    .then(response => {
                      this.references[index].property = response.properties[0]
                    })
                }
              }
            })
        }
        if (element.startsWith('request')) {
          RequestApi.listByIds([element])
            .then(response => {
              this.references[index].request = response.requests[0]
              ObjectApi.getObject(this.references[index].request.objectId)
                .then(response => {
                  this.references[index].object = response.object
                  ObjectApi.getBuildingByObjectId(this.references[index].object.id).then(response => {
                    this.references[index].building = response.building
                    ObjectApi.getPropertiesByBuildingIds([this.references[index].building.id])
                      .then(response => {
                        this.references[index].property = response.properties[0]
                      })
                  }).catch(e => {
                    Vue.toasted.show(this.$t('message.loadingErrors.advertisement'), { type: 'error' })
                  })
                })
              const cont = response.requests[0].requesters[0].contactId
              if (cont.length > 0) {
                ContactApi.contactResolve([cont])
                  .then(response => {
                    this.references[index].listContact = ([...response.persons, ...response.companies] as Contact[]).filter(c => cont.includes(c.id))
                  })
              }
            })
        }
        if (element.startsWith('obj')) {
          ObjectApi.getObject(element)
            .then(response => {
              this.references[index].object = response.object
              ObjectApi.getBuildingByObjectId(this.references[index].object.id).then(response => {
                this.references[index].building = response.building
                ObjectApi.getPropertiesByBuildingIds([this.references[index].building.id])
                  .then(response => {
                    this.references[index].property = response.properties[0]
                  })
              }).catch(e => {
                console.log(e)
                Vue.toasted.show(this.$t('message.loadingErrors.advertisement'), { type: 'error' })
              })
            })
        }
        if (element.startsWith('room')) {
          RoomApi.listById([element]).then(roomResponse => {
            const room = roomResponse.rooms[0]
            this.references[index].room = room
            if (room && room.referenceId.startsWith('obj')) {
              ObjectApi.getObject(room.referenceId)
                .then(response => {
                  this.references[index].object = response.object
                  ObjectApi.getBuildingByObjectId(this.references[index].object.id).then(response => {
                    this.references[index].building = response.building
                    ObjectApi.getPropertiesByBuildingIds([this.references[index].building.id])
                      .then(response => {
                        this.references[index].property = response.properties[0]
                      })
                  }).catch(e => {
                    console.log(e)
                    Vue.toasted.show(this.$t('message.loadingErrors.advertisement'), { type: 'error' })
                  })
                })
            }
            if (room && room.referenceId.startsWith('building')) {
              ObjectApi.getBuilding(room.referenceId).then(response => {
                this.references[index].building = response.building
                ObjectApi.getPropertiesByBuildingIds([this.references[index].building.id])
                  .then(response => {
                    this.references[index].property = response.properties[0]
                  })
              }).catch(e => {
                console.log(e)
                Vue.toasted.show(this.$t('message.loadingErrors.advertisement'), { type: 'error' })
              })
            }
          })
        }
        if (element.startsWith('building')) {
          ObjectApi.getBuildingsListByIds([element]).then(response => {
            this.references[index].building = response.buildings[0]
            ObjectApi.getPropertiesByBuildingIds([this.references[index].building.id])
              .then(response => {
                this.references[index].property = response.properties[0]
              })
          })
        }
        if (element.startsWith('prop')) {
          ObjectApi.getPropertyListByIds([element])
            .then(response => {
              this.references[index].property = response.properties[0]
            })
        }
        if (element.startsWith('contact')) {
          ContactApi.contactResolve([element])
            .then(response => {
              this.references[index].contact = ([...response.persons, ...response.companies] as Contact[]).find(x => x.id === element)
            })
        }
        if (element.startsWith('mortgage')) {
          ObjectApi.getMortgagesByIds([element])
            .then(response => {
              if (response.mortgages.length > 0) {
                this.references[index].mortgage = response.mortgages[0]
                if (response.mortgages[0].referenceId?.startsWith('building')) {
                  ObjectApi.getBuilding(response.mortgages[0].referenceId)
                    .then(buildingResponse => {
                      this.references[index].building = buildingResponse.building
                      ObjectApi.getPropertiesByBuildingIds([buildingResponse.building.id])
                        .then(propertResponse => {
                          this.references[index].property = propertResponse.properties[0]
                        })
                    })
                }
                if (response.mortgages[0].referenceId?.startsWith('prop')) {
                  ObjectApi.getPropertyListByIds([response.mortgages[0].referenceId])
                    .then(propertyResponse => {
                      this.references[index].property = propertyResponse.properties[0]
                    })
                }
              }
            })
        }
        if (element.startsWith('ins')) {
          ObjectApi.getInsurancesByIds([element])
            .then(response => {
              if (response.insurances.length > 0) {
                this.references[index].insurance = response.insurances[0]
                if (response.insurances[0].referenceId?.startsWith('building')) {
                  ObjectApi.getBuilding(response.insurances[0].referenceId)
                    .then(buildingResponse => {
                      this.references[index].building = buildingResponse.building
                      ObjectApi.getPropertiesByBuildingIds([buildingResponse.building.id])
                        .then(propertResponse => {
                          this.references[index].property = propertResponse.properties[0]
                        })
                    })
                }
                if (response.insurances[0].referenceId?.startsWith('prop')) {
                  ObjectApi.getPropertyListByIds([response.insurances[0].referenceId])
                    .then(propertyResponse => {
                      this.references[index].property = propertyResponse.properties[0]
                    })
                }
              }
            })
        }
      }
    },
    removeReference(index) {
      this.listEntities = this.listEntities.filter((element, i) => i !== index)
      this.$delete(this.references, index)
    },
    getReferenceById(id, index) {
      const item = this.references[index]
      if (!item || !id) {
        return null
      }
      if (id.startsWith('prop')) {
        return item.property
      } else if (id.startsWith('building')) {
        return item.building
      } else if (id.startsWith('obj') || id.startsWith('condominium') || id.startsWith('tenancy')) {
        return item.object
      }
      return null
    },
    onSelectLinked(selected) {
      if (selected) {
        if (!this.checkIsDisabled(selected.item)) {
          this.ticketToLink = selected.item.id
          this.searchForTickets = null
          this.$nextTick(function () {
            this.refrecheAutosuggestKey = Math.floor(Math.random() * 100)
          })
        } else {
          this.$nextTick(function () {
            this.searchForTickets = null
            this.ticketToLink = ''
            this.ticketsForLink = []
          })
        }
        this.linkToTicket()
      }
    },
    deleteMedia(e, id) {
      e.stopPropagation()
      if (this.ticket.mediaIds) {
        this.ticket.mediaIds = this.ticket.mediaIds.filter(e => e !== id)
      }
      if (this.mediasList) {
        this.mediasList = this.mediasList.filter(el => el.id !== id)
        this.initCount()
      }
    },
    async addFile(event) {
      this.attachment = await this.getFilesFromEvent(event)
      if (this.attachment?.length > 0) {
        await this.sendFile()
        this.$store.dispatch('user/setChangedModal', true).then().catch(e => console.log(e))
      }
    },
    async sendFile() {
      this.uploading = true
      this.disableBtns = true
      const response = await this.uploadMedia(this.attachment, '', 'true')
      if (response?.length > 0) {
        for (const media of response) {
          this.mediasList.push(media)
          this.ticket.mediaIds.push(media.id)
        }
        this.$nextTick(() => {
          this.attachment = []
          this.uploading = false
          this.disableBtns = false
          this.initCount()
          if (this.$refs['coozzy-file-input']) {
            this.$refs['coozzy-file-input'].resetPlaceholder()
          }
        })
      } else {
        this.uploading = false
        this.disableBtns = false
      }
    },
    cancelClicked() {
      if (this.hasChanged === true) {
        this.$refs.confirmationModal.show()
      } else {
        this.confirmCancellation()
      }
    },
    confirmCancellation() {
      this.hasChanged = false
      if (this.nextToPath !== null) {
        this.$router.push({
          name: this.nextToPath.name,
          params: this.nextToPath.params
        }, () => {
        })
      } else {
        if ((this.prevRoutePath.includes('buildings/building_') || this.prevRoutePath.includes('properties/prop_')) && this.references.length > 0 && this.references[0].device) {
          this.redirectDevicePage(this.references[0])
        } else if (this.prevRoutePath !== '') {
          this.$router.replace({
            path: this.prevRoutePath,
            query: this.prevRouteQuery
          }, () => {
          })
        } else {
          this.redirectToDetailPage(this.ticket.key)
        }
      }
    },
    proceedSave(omitNotification) {
      this.$refs.informOperatorModal.hide()
      this.showInformOperatorModal = false
      this.executeTicketUpdate(this.pendingUpdateMask, this.pendingDraft, omitNotification)
    },
    executeTicketUpdate(updateMask, draft, omitCorrespondenceNotification) {
      TicketApi.updateTicket(this.ticket, updateMask, omitCorrespondenceNotification)
        .then(async () => {
          await this.applyPendingLinkedEmailUnlinks(this.ticket.id)
          this.hasChanged = false
          if (!draft) {
            this.redirectToDetailPage(this.ticket.key)
          } else {
            this.redirectToTicketDraft()
          }
          Vue.toasted.show(this.$t('message.successMessages.ticket'), { type: 'success' })
        })
        .catch(e => {
          console.log(e)
          const err = e.response && e.response.data
          if (err && err.code === 2 && err.message) {
            Vue.toasted.show(err.message, { type: 'error' })
          } else {
            Vue.toasted.show(this.$t('message.savingErrors.ticket'), { type: 'error' })
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    removeBuilding(buildingIndex) {
      this.property.buildings.splice(buildingIndex, 1)
    },
    getListRole(contact) {
      if (contact !== null && contact.types !== undefined) {
        if (contact.types && contact.types.length === 1 && contact.types[0] === 'NONE') {
          return '-'
        } else {
          const array = []
          contact.types.forEach(role => {
            if (role !== 'NONE') {
              array.push(this.$t('message.contact.types.' + role.toUpperCase()))
            }
          })
          return array.join(', ')
        }
      }
      return '-'
    },
    indicesToSuggestions(indices, contactHits) {
      const algoliaGroups = this.contactsOnly ? [] : indices.map(({ hits }) => ({ data: hits }))
      return this.includesContacts ? algoliaGroups.concat([{ data: contactHits }]) : algoliaGroups
    },
    onChange(refine, refineContacts, email) {
      if (email && email.length > 0) {
        refineContacts(email)
        if (email !== '') clearTimeout(this.debounceTimeout)
        this.debounceTimeout = setTimeout(() => {
          this.debounceTimeout = null
          refine(email)
        }, 300)
      }
    },
    getConnectionObjectCategoryLabel(connection) {
      const objectCategory = connection?.obj?.category || connection?.objectCategory || connection?.obj?.objectCategory || connection?.object?.objectCategory || connection?.category || connection?.object?.category
      if (!objectCategory) {
        return '-'
      }
      const objectCategoryKey = String(objectCategory).trim()
      const translationKey = `message.onBoarding.buildings.objects.category.${objectCategoryKey}`
      return this.$te(translationKey) ? this.$t(translationKey) : objectCategoryKey
    },
    dateToObject() {
      this.ticket.startDate = this.toObject(this.dates.startDate)
      this.ticket.dueDate = this.toObject(this.dates.dueDate)
    },
    linkToTicket() {
      if (this.ticketToLink !== '') {
        const linkedTicket = this.ticketsForLink.find(w => w.id === this.ticketToLink)
        this.listLinks.push({
          ticketId: this.ticketToLink,
          type: this.typeOfLink
        })
        if (linkedTicket) {
          this.listTickets.push(linkedTicket)
        }
        this.typeOfLink = 'TICKET_LINK_TYPE_RELATES'
        this.ticketToLink = ''
        this.searchForTickets = ''
      }
    },
    removeTicketLinked(ticket) {
      this.listLinks = this.listLinks.filter(x => !(x.ticketId === ticket.ticketId && x.type === ticket.type))
    },
    see(refine, value) {
      refine(value)
    },
    customLabel(item) {
      return item.name
    },
    redirectDevicePage(item) {
      if (this.prevRoutePath.includes('buildings/building_')) {
        const idBuilding = item.building?.id ? item.building.id : item.building.objectID
        const view = this.prevRouteQuery.view || ''
        if (this.isOwnerModule) {
          this.$router.push({
            name: 'OwnerBuildingDetailsView',
            params: { id: idBuilding },
            query: { view: view }
          }, () => {
          })
        } else if (this.isAdminModule) {
          this.$router.push({
            name: 'AdminBuildingDetailsView',
            params: { id: idBuilding },
            query: { view: view }
          }, () => {
          })
        } else if (this.isAssetModule) {
          this.$router.push({
            name: 'AssetBuildingDetailsView',
            params: { id: idBuilding },
            query: { view: view }
          }, () => {
          })
        } else if (this.isAccountingModule) {
          this.$router.push({
            name: 'AccountingBuildingDetailsView',
            params: { id: idBuilding },
            query: { view: view }
          }, () => {
          })
        }
      } else if (this.prevRoutePath.includes('properties/prop_')) {
        const idProperty = item.property.id ? item.property.id : item.property.objectID
        const view = this.prevRouteQuery.view || ''
        if (this.isOwnerModule) {
          this.$router.push({
            name: 'OwnerPropertyDetailsView',
            params: { id: idProperty },
            query: { view: view }
          }, () => {
          })
        } else if (this.isAdminModule) {
          this.$router.push({
            name: 'AdminPropertyDetailsView',
            params: { id: idProperty },
            query: { view: view }
          }, () => {
          })
        } else if (this.isAssetModule) {
          this.$router.push({
            name: 'AssetPropertyDetailsView',
            params: { id: idProperty },
            query: { view: view }
          }, () => {
          })
        } else if (this.isAccountingModule) {
          this.$router.push({
            name: 'AccountingPropertyDetailsView',
            params: { id: idProperty },
            query: { view: view }
          }, () => {
          })
        }
      }
    },
    redirectToDetailPage(ticketKey = null) {
      if (ticketKey) {
        if (this.isOwnerModule) {
          this.$router.replace({
            name: 'OwnerTicketDetailsView',
            params: { id: ticketKey }
          }, () => {
          })
        } else if (this.isAdminModule) {
          this.$router.replace({
            name: 'AdminTicketDetailsView',
            params: { id: ticketKey }
          }, () => {
          })
        } else if (this.isAssetModule) {
          this.$router.replace({
            name: 'AssetTicketDetailsView',
            params: { id: ticketKey }
          }, () => {
          })
        } else if (this.isAccountingModule) {
          this.$router.replace({
            name: 'AccountingTicketDetailsView',
            params: { id: ticketKey }
          }, () => {
          })
        } else {
          this.$router.replace({
            name: 'MarketingTicketDetailsView',
            params: { id: ticketKey }
          }, () => {
          })
        }
      } else {
        if (this.isOwnerModule) {
          this.$router.push({ name: 'OwnerTicketOverview' }, () => {
          })
        } else if (this.isAdminModule) {
          this.$router.push({ name: 'AdminTicketOverview' }, () => {
          })
        } else if (this.isAssetModule) {
          this.$router.push({ name: 'AssetTicketOverview' }, () => {
          })
        } else if (this.isAccountingModule) {
          this.$router.push({ name: 'AccountingTicketOverview' }, () => {
          })
        } else {
          this.$router.push({ name: 'MarketingTicketOverview' }, () => {
          })
        }
      }
    },
    getReferenceEntityId(entity) {
      if (!entity) {
        return null
      }
      return entity.id || entity.objectID || null
    },
    getLinkedObjectId(source) {
      if (!source) {
        return null
      }
      return source.obj?.id || source.objectId || source.objectID || source.object?.id || null
    },
    filterRelatedIds(ids) {
      return ids.filter(id => id != null && id !== '')
    },
    async ensureReferencesResolved() {
      await Promise.all(this.references.map(async (element) => {
        if ((element.tenancy || element.condominium) && !element.object) {
          const objectId = this.getLinkedObjectId(element.tenancy) || this.getLinkedObjectId(element.condominium)
          if (objectId) {
            try {
              const response = await ObjectApi.getObject(objectId)
              element.object = response.object
            } catch (e) {
              console.log(e)
            }
          }
        }
        if (element.device?.referenceId?.startsWith('obj') && !element.object) {
          try {
            const response = await ObjectApi.getObject(element.device.referenceId)
            element.object = response.object
          } catch (e) {
            console.log(e)
          }
        }
        const objectId = this.getReferenceEntityId(element.object)
        if (objectId?.startsWith('obj')) {
          try {
            const response = await ObjectApi.getObject(objectId)
            element.object = response.object
          } catch (e) {
            console.log(e)
          }
        }
        if (element.object && !element.building) {
          const objId = this.getReferenceEntityId(element.object)
          try {
            const response = await ObjectApi.getBuildingByObjectId(objId)
            element.building = response.building
          } catch (e) {
            console.log(e)
          }
        }
        if (element.building && !element.property) {
          const buildingId = this.getReferenceEntityId(element.building)
          try {
            const response = await ObjectApi.getPropertiesByBuildingIds([buildingId])
            element.property = response.properties?.[0] || null
          } catch (e) {
            console.log(e)
          }
        }
        if (element.room?.referenceId?.startsWith('obj') && !element.object) {
          try {
            const response = await ObjectApi.getObject(element.room.referenceId)
            element.object = response.object
            const buildingResponse = await ObjectApi.getBuildingByObjectId(element.room.referenceId)
            element.building = buildingResponse.building
            if (element.building) {
              const propertyResponse = await ObjectApi.getPropertiesByBuildingIds([element.building.id])
              element.property = propertyResponse.properties?.[0] || null
            }
          } catch (e) {
            console.log(e)
          }
        }
      }))
    },
    buildTicketReferencesFromElements({ trackOwnerId = true, includeContactIds = true } = {}) {
      const ticketReferences = []
      let ownerId = ''
      this.references.forEach((element, index) => {
        const contactIds = includeContactIds && element.listContact ? element.listContact.map(contact => contact.id) : []
        if (element.mortgage) {
          if (trackOwnerId && index === 0) {
            ownerId = 'ownerId' in element.mortgage ? element.mortgage.ownerId : element.mortgage.owner?.id
          }
          ticketReferences.push({
            id: this.getReferenceEntityId(element.mortgage),
            relatedIds: this.filterRelatedIds([element.mortgage.referenceId, ...contactIds])
          })
        } else if (element.insurance) {
          if (trackOwnerId && index === 0) {
            ownerId = 'ownerId' in element.insurance ? element.insurance.ownerId : element.insurance.owner?.id
          }
          ticketReferences.push({
            id: this.getReferenceEntityId(element.insurance),
            relatedIds: this.filterRelatedIds([element.insurance.referenceId, ...contactIds])
          })
        } else if (element.room && !element.device) {
          if (trackOwnerId && index === 0) {
            ownerId = 'ownerId' in element.room ? element.room.ownerId : element.room.owner?.id
          }
          let ids = []
          if (element.object) {
            ids = [
              this.getReferenceEntityId(element.property),
              this.getReferenceEntityId(element.building),
              this.getReferenceEntityId(element.object)
            ]
          } else if (element.building) {
            ids = [
              this.getReferenceEntityId(element.property),
              this.getReferenceEntityId(element.building)
            ]
          } else {
            ids = [element.room.referenceId]
          }
          ticketReferences.push({
            id: this.getReferenceEntityId(element.room),
            relatedIds: this.filterRelatedIds([...ids, ...contactIds])
          })
        } else if (element.contact) {
          if (trackOwnerId && index === 0) {
            ownerId = 'ownerId' in element.contact ? element.contact.ownerId : element.contact.owner?.id
          }
          ticketReferences.push({
            id: this.getReferenceEntityId(element.contact),
            relatedIds: []
          })
        } else if (element.tenancy) {
          if (trackOwnerId && index === 0) {
            ownerId = 'ownerId' in element.tenancy ? element.tenancy.ownerId : element.tenancy.owner?.id
          }
          ticketReferences.push({
            id: this.getReferenceEntityId(element.tenancy),
            relatedIds: this.filterRelatedIds([
              this.getReferenceEntityId(element.property),
              this.getReferenceEntityId(element.building),
              this.getReferenceEntityId(element.object),
              ...contactIds
            ])
          })
        } else if (element.request) {
          if (trackOwnerId && index === 0) {
            ownerId = 'ownerId' in element.request ? element.request.ownerId : element.request.owner?.id
          }
          ticketReferences.push({
            id: this.getReferenceEntityId(element.request),
            relatedIds: this.filterRelatedIds([
              this.getReferenceEntityId(element.property),
              this.getReferenceEntityId(element.building),
              this.getReferenceEntityId(element.object),
              ...contactIds
            ])
          })
        } else if (element.condominium) {
          if (trackOwnerId && index === 0) {
            ownerId = 'ownerId' in element.condominium ? element.condominium.ownerId : element.condominium.owner?.id
          }
          ticketReferences.push({
            id: this.getReferenceEntityId(element.condominium),
            relatedIds: this.filterRelatedIds([
              this.getReferenceEntityId(element.property),
              this.getReferenceEntityId(element.building),
              this.getReferenceEntityId(element.object),
              ...contactIds
            ])
          })
        } else if (element.device) {
          if (trackOwnerId && index === 0) {
            ownerId = element.device.ownerId
          }
          let ids = []
          if (element.object) {
            ids = [
              this.getReferenceEntityId(element.property),
              this.getReferenceEntityId(element.building),
              this.getReferenceEntityId(element.object)
            ]
          } else if (element.building) {
            ids = [
              this.getReferenceEntityId(element.property),
              this.getReferenceEntityId(element.building)
            ]
          } else {
            ids = [this.getReferenceEntityId(element.property)]
          }
          ticketReferences.push({
            id: this.getReferenceEntityId(element.device),
            relatedIds: this.filterRelatedIds([...ids, ...contactIds])
          })
        } else if (element.object && !element.tenancy && !element.condominium) {
          if (trackOwnerId && index === 0) {
            ownerId = 'ownerId' in element.object ? element.object.ownerId : element.object.owner?.id
          }
          ticketReferences.push({
            id: this.getReferenceEntityId(element.object),
            relatedIds: this.filterRelatedIds([
              this.getReferenceEntityId(element.property),
              this.getReferenceEntityId(element.building),
              ...contactIds
            ])
          })
        } else if (element.building) {
          if (trackOwnerId && index === 0) {
            ownerId = 'ownerId' in element.building ? element.building.ownerId : element.building.owner?.id
          }
          ticketReferences.push({
            id: this.getReferenceEntityId(element.building),
            relatedIds: this.filterRelatedIds([
              this.getReferenceEntityId(element.property),
              ...contactIds
            ])
          })
        } else if (element.property) {
          if (trackOwnerId && index === 0) {
            ownerId = 'ownerId' in element.property ? element.property.ownerId : element.property.owner?.id
          }
          ticketReferences.push({
            id: this.getReferenceEntityId(element.property),
            relatedIds: this.filterRelatedIds([...contactIds])
          })
        }
      })
      return { ticketReferences, ownerId }
    },
    async saveTicket(draft = false) {
      const skipOrderMandatory = draft && this.ticket.type === 'TICKET_TYPE_ORDER'
      this.$v.ticket.$touch()
      if (this.$v.ticket.dueDate.$dirty && this.$v.ticket.dueDate.$error) {
        if (!skipOrderMandatory && !this.$v.ticket.dueDate.required) {
          Vue.toasted.show(this.$t('message.savingErrors.executeUntilRequired'), { type: 'error' })
        } else if (!this.$v.ticket.dueDate.greaterThanStartDate) {
          Vue.toasted.show(this.$t('message.savingErrors.executeUntilBeforeExecuteFrom'), { type: 'error' })
        }
      }
      const isInvalid = skipOrderMandatory
        ? !this.$v.ticket.dueDate.greaterThanStartDate
        : this.$v.ticket.$invalid
      if (!isInvalid) {
        if (this.ticket.type === 'TICKET_TYPE_ORDER' &&
            this.ticket.correspondences.length > 0 &&
            !this.ticket.correspondences[0].email) {
          Vue.toasted.show(this.$t('message.savingErrors.operatorHasNoEmail'), { type: 'error' })
          return
        }
        this.loading = true
        this.ticket.assignee = this.ticket.assignee && this.ticket.assignee.value
          ? {
            accountId: this.ticket.assignee.value.accountId,
            userId: this.ticket.assignee.value.id
          }
          : null
        const corres = []
        if (this.ticket.type !== 'TICKET_TYPE_ORDER') {
          this.ticket.correspondences.forEach(element => {
            corres.push({ contactId: element.objectID || element.contactId })
          })
          this.ticket.correspondences = corres
        } else {
          const cor = this.ticket.correspondences.length > 0 ? { contactId: this.ticket.correspondences[0].objectID || this.ticket.correspondences[0].id || this.ticket.correspondences[0].contactId } : null
          this.ticket.correspondences = cor ? [cor] : []
        }
        if (!this.id) {
          await this.ensureReferencesResolved()
          this.rebuildGeneratedDescription()
          this.ticket.description += this.generatedDescription
        }

        const tasks = JSON.parse(JSON.stringify(this.listTasks))
        this.ticket.checklist = tasks.map(x => {
          const taskAssignee = x.assignee
          const assignee = taskAssignee ? { accountId: taskAssignee.value.accountId, userId: taskAssignee.value.id } : null
          delete x.assignee
          return {
            assignee: assignee,
            ...x
          }
        })

        await this.ensureReferencesResolved()
        let ownerId = ''
        let ticketReferences = []
        if (this.references.length > 0) {
          const builtReferences = this.buildTicketReferencesFromElements({ trackOwnerId: true })
          ticketReferences = builtReferences.ticketReferences
          ownerId = builtReferences.ownerId
        } else {
          ownerId = this.$store.getters['user/getAccountId']
        }
        this.ticket.ownerId = this.ticket.id ? this.ticket.ownerId : ownerId
        this.ticket.references = ticketReferences
        if (this.ticket.onSiteContacts.length > 0) {
          const list = []
          this.onSiteContactPersons.forEach(contact => {
            list.push({
              contactId: contact.objectID || contact.id || contact.contactId,
              notify: false,
              notifyOnlyStatusChanges: false,
              includeCorrespondenceContact: false
            })
          })
          this.ticket.onSiteContacts = list
          this.ticket.onSiteContact = null
        } else if (this.ticket.onSiteContact) {
          this.ticket.onSiteContacts.push({
            contactId: this.ticket.onSiteContact.objectID || this.ticket.onSiteContact.id || this.ticket.onSiteContact.contactId,
            notify: false,
            notifyOnlyStatusChanges: false,
            includeCorrespondenceContact: false
          })
        }
        this.ticket.links = [...this.listLinks]
        if (this.ticket.id) {
          if (draft) {
            this.ticket.status = 'TICKET_STATUS_DRAFT'
          } else {
            this.ticket.status = this.ticket.status !== 'TICKET_STATUS_DRAFT' ? this.ticket.status : 'TICKET_STATUS_OPEN'
          }
          let updateMask = null
          if (this.source === 'changeOperator' && !draft) {
            updateMask = 'id,assignee,correspondences'
          }
          if (this.linksUpdated) {
            updateMask = updateMask ? `${updateMask},links` : 'id,links'
          }
          if (this.ticket.correspondences.length > 0) {
            this.pendingDraft = draft
            this.pendingUpdateMask = updateMask
            this.showInformOperatorModal = true
            this.$nextTick(() => {
              this.$refs.informOperatorModal.show()
            })
            return
          }
          this.executeTicketUpdate(updateMask, draft, false)
        } else {
          let promises = []
          if (this.listRequestsMedia.length > 0) {
            this.listRequestsMedia.forEach(element => {
              if (element !== null) {
                const formData = new FormData()
                const fileName = this.editFileName(element.filename)
                formData.append('file', element.blob, fileName)
                formData.append('filename', element.filename)
                formData.append('public', 'true')
                promises.push(MediaApi.uploadMedia(formData))
              }
            })
            await Promise.all(promises)
              .then(response => {
                for (const media of response) {
                  this.ticket.mediaIds.push(media.id)
                }
              })
          }
          if (this.ticket.description.includes('#anrede#') || this.ticket.description.includes('#eigentuemer#') || this.ticket.description.includes('#eigentuemer.addresse#')) {
            this.ticket.description = await this.translatePlaceholders(this.ticket.description)
          }
          if (draft) {
            this.ticket.status = 'TICKET_STATUS_DRAFT'
          } else {
            this.ticket.status = 'TICKET_STATUS_OPEN'
          }
          TicketApi.newTicket(this.ticket)
            .then((response) => {
              this.ticket = response.ticket
              if (this.emailsDataForLinking && this.emailsDataForLinking.id) {
                const id = this.emailsDataForLinking.id
                MessageApi.setReferenceIds([id], [this.ticket.id].concat(this.emailsDataForLinking.referenceIds || []))
                  .then(() => {
                    this.hasChanged = false
                    if (!draft) {
                      this.pageRedirection()
                    } else {
                      this.redirectToTicketDraft()
                    }
                  })
              } else {
                this.hasChanged = false
                this.pageRedirection()
              }
            })
            .catch(e => {
              console.log(e)
              Vue.toasted.show(this.$t('message.savingErrors.ticket'), { type: 'error' })
            })
            .finally(() => {
              this.loading = false
            })
        }
      } else {
        this.scrollToError()
      }
    },
    redirectToTicketDraft() {
      if (this.isOwnerModule) {
        this.$router.replace({
          name: 'OwnerDraftTicketOverview'
        })
      } else if (this.isAdminModule) {
        this.$router.replace({
          name: 'AdminDraftTicketOverview'
        })
      } else if (this.isAssetModule) {
        this.$router.replace({
          name: 'AssetDraftTicketOverview'
        })
      } else if (this.isAccountingModule) {
        this.$router.replace({
          name: 'AccountingDraftTicketOverview'
        })
      } else {
        this.$router.replace({
          name: 'MarketingDraftTicketOverview'
        })
      }
    },
    pageRedirection() {
      if (this.ticket.type === 'TICKET_TYPE_ORDER') {
        Vue.toasted.show(this.$t('message.successMessages.orderSent'), { type: 'success' })
      } else {
        Vue.toasted.show(this.$t('message.successMessages.ticket'), { type: 'success' })
      }
      if (this.fromBulk === true && this.references.length > 0 && this.references[0].device && (this.prevRoutePath.includes('buildings/building_') || this.prevRoutePath.includes('properties/prop_'))) {
        this.redirectDevicePage(this.references[0])
      } else if (typeof this.ids === 'string' && this.ids.startsWith('request_')) {
        const objectId = this.references[0].object?.id
        const requestId = this.ids
        this.pushToRequestDetail(objectId, requestId)
      } else {
        this.redirectToDetailPage(this.ticket.key)
      }
    },
    editTask(task) {
      this.editingTask = { ...task }
    },
    editTaskNote(task) {
      this.editingTaskNote = { ...task }
    },
    finishEdit(task) {
      if (task) {
        let taskIndex = this.listTasks.findIndex(
          (r) => r.id === task.id
        )
        if (taskIndex !== -1) {
          this.listTasks.splice(taskIndex, 1, task)
        }
      }
      this.editingTask = null
    },
    deleteTask(task) {
      this.listTasks = this.listTasks.filter(r => task.id !== r.id)
      this.initialTasks = this.listTasks.map(r => r.title)
      this.updateOrder()
    },
    checklistAdded(list) {
      if (list.length > 0) {
        const item = list[list.length - 1]
        const internalId = Date.now() + Math.floor(Math.random() * 1000)
        const id = JSON.parse(JSON.stringify('task_' + internalId))
        const assignee = this.listAllEmployees.find(y => y.value.id === this.userId)
        const task = {
          id: id,
          title: item,
          note: '',
          order: 1,
          status: 'CHECKLIST_ITEM_STATUS_OPEN',
          assignee: assignee
        }
        this.listTasks.push(task)
        this.initialTasks = []
        this.updateOrder()
      }
    },
    watchersChanged(watchers) {
      this.ticket.watchers = watchers
      this.ticketPreview.watchers = watchers
    },
    checklistNoteChanged(task) {
      const oldTask = this.listTasks.find(x => x.id === task.id)
      if (task) {
        let taskIndex = this.listTasks.findIndex(
          (r) => r.id === task.id
        )
        if (taskIndex !== -1) {
          this.listTasks.splice(taskIndex, 1, {
            ...oldTask,
            note: task.note
          })
        }
      }
    },
    checklistStatusChanged(task) {
      const oldTask = this.listTasks.find(x => x.id === task.id)
      if (task) {
        let taskIndex = this.listTasks.findIndex(
          (r) => r.id === task.id
        )
        if (taskIndex !== -1) {
          this.listTasks.splice(taskIndex, 1, {
            ...oldTask,
            status: task.status
          })
        }
      }
    },
    checklistAssigneeChanged(assigneeId, taskId) {
      const assignee = assigneeId ? this.listAllEmployees.find(y => y.value.id === assigneeId) : null
      const task = this.listTasks.find(x => x.id === taskId)
      if (task) {
        const taskIndex = this.listTasks.findIndex(
          (r) => r.id === task.id
        )
        if (taskIndex !== -1) {
          this.listTasks.splice(taskIndex, 1, {
            ...task,
            assignee: assignee
          })
        }
      }
    },
    suggestionRemoved(item) {
      this.listTasks = this.listTasks.filter(x => x.id !== item)
    },
    updateOrder() {
      // Update the order attribute based on the new arrangement
      this.listTasks.forEach((task, index) => {
        task.order = index + 1
      })
    },
    saveTaskNote(e) {
      // Prevent new line on Enter in textarea for task note
      if (e && e.preventDefault) {
        e.preventDefault()
      }
      this.editingTaskNote = null
    }
  },
  validations() {
    return {
      ticket: {
        type: {
          required
        },
        assignee: {
          required
        },
        title: {
          required
        },
        dueDate: {
          required: requiredIf(function() {
            return this.isDueDateRequired
          }),
          greaterThanStartDate
        }
      }
    }
  }
})
</script>
<style lang="scss" scoped>

:deep(.textAreaHeight textarea) {
  height: calc(1.5em + 0.5rem + 2px);
}

:deep(.vs__dropdown-toggle) {
  height: calc(1.5em + 0.5rem + 2px);
  padding-top: 0;
  padding-left: 0.25rem;
  font-size: 0.875rem;
  vertical-align: middle;
  background-color: #fff;
  border: 1px solid #ced4da;
  box-shadow: 1px 1px 6px #ccc;
}

:deep(.vs__selected) {
  margin: 0 2px 0;

  img {
    margin-bottom: 2px;
  }
}

:deep(.vs__clear) {
  box-shadow: none;
}

.thumbnail-size-restriction {
  width: 100%;
  height: auto;
  max-height: 3rem;
}

.linked-row {
  border-radius: 0.2rem;
  background-color: white;
  border: 1px solid $color-input-border;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out, -webkit-box-shadow 0.15s ease-in-out;

  .label {
    line-height: 1.8rem;
  }

  .status-design {
    min-width: 125px !important;
    text-align: center;
    padding: 0.35rem 0.5rem;
    text-transform: uppercase;
    font-size: small;
    border-radius: 0.2rem;

    &.open {
      background: lightgrey;
    }

    &.in_progress {
      background: lightskyblue;
    }

    &.done {
      background: darkseagreen;
    }
  }
}

.uploaded-file {
  max-height: 100px;
  background-position: center center;
  background-size: cover;
  min-height: 5em;
}

.file-block {
  display: flex;
  align-items: center;
}

.delete-icon.image {
  position: absolute;
  right: 15px;
}

.delete-icon.video {
  position: absolute;
  right: 15px;
  top: 5px;
}

.color-box.condo_owner {
  background: #f589c7;
}

.color-box.condominium {
  background: #913489;
}

.color-box.tenancy {
  background: #00a4bd;
}

.color-box.object {
  background: #f38d38;
}

.color-box.device {
  background: #767171;
}

.color-box.request {
  background: #f7b6ae;
}

.containerCircles {
  height: 21px;
  align-items: center;
  display: flex;
}

.subtitle {
  color: #495057;
  font-weight: 400;
}

.media-block {
  padding: 0.5rem;
  padding-left: 0;
}

@media (min-width: 768px) {
  .uploaded-file {
    max-height: 100px;
    min-height: 4em;
  }
}

@media (min-width: 992px) {
  .uploaded-file {
    max-height: 110px;
    min-height: 5em;
  }
}

@media (min-width: 1200px) {
  .uploaded-file {
    max-height: 120px;
    min-height: 5.5em;
  }
}

@media (min-width: 1600px) {
  .uploaded-file {
    max-height: 130px;
    min-height: 7em;
  }
}

.showSelected {
  display: none;
}

.selected {
  background-color: rgba(0, 0, 0, 0.08);
}

.selected .showSelected {
  display: block;
  color: rgba(0, 0, 0, 0.5);
  align-self: center;
  margin-right: 20px;
  font-weight: 600;
}

.disableOptionHover {
  cursor: default !important;
  color: #6c757d !important;
  opacity: 0.5;
}

:deep(#autosuggest .autosuggest__results) {
  z-index: 9998;
}

.tooltip {
  z-index: 9999;
}

// .bg-init {
//   background: initial !important;
// }
:deep(.assignee .vs__selected-options) {
  width: 70%;
}

:deep(.assignee .vs__selected-options .vs__selected > div) {
  width: calc(22vw);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.connection a.router-link-active, .connection a {
  color: inherit !important;
  text-decoration: none !important;
}

.connection a.router-link-active:hover, .connection a:hover {
  color: #0055b7 !important;
  text-decoration: underline !important;
  cursor: pointer;
}

:deep(.autosuggest__results-item.autosuggest__results-item--highlighted) {
  background-color: rgba(0, 0, 0, 0.03);
}

:deep(.connection-device-search-input) {
  height: calc(1.5em + 0.5rem + 2px);
  font-size: 0.875rem;
  line-height: 1.5;
  padding: 0.25rem 0.5rem;
}

.device-option-title {
  font-size: 14px;
  font-weight: 700;
}

.device-option-date {
  font-size: 14px;
  font-weight: 400;
}

:deep .is-invalid .vs__dropdown-toggle {
  border-color: #dc3545;
  padding-right: calc(1.5em + 0.75rem) !important;
  background-image: url("../../../assets/icon/is-invalid.svg");
  background-repeat: no-repeat;
  background-position: right calc(0.375em + 0.1875rem) center;
  background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem);
}

.wrap {
  white-space: pre-wrap;
  word-wrap: break-word;
  word-break: break-all;
}

.sizeLabel {
  font-size: 0.875rem !important;
}

.wizard-btn {
  background-color: rgb(0, 68, 95) !important;
  border-color: rgb(0, 68, 95) !important;
  color: white !important;
}

.autosuggest__results-item .selected {
  color: rgba(0, 0, 0, 0.5) !important;
}

:deep(.tooltip.b-tooltip) {
  z-index: 9999;
}

.selectedOption {
  color: #495057;
  background-color: #e9ecef;
}

.item-task {
  padding: 10px 0;
  margin-bottom: 0.25rem;
  margin-top: 0.25rem;
  border: 1px solid #ddd;
  border-radius: 3px;
  background-color: #fdfdfd;

  p, .input-task {
    margin: 0;
    width: 95%;
  }
}

.item-task:first-child {
  margin-top: 1rem;
}
:deep(.v-select.assignee .vs__selected) {
  word-break: break-word;
  overflow: hidden;
  text-overflow: ellipsis;
}
.container_textArea {
  position: relative;
  display: flex;
  .icon {
    position: relative;
    height: 30px;
    width: 35px;
    margin-left: 10px;
  }
}
</style>
